// Firebase Firestore Database Service Layer
// Equivalent to lib/database.ts but for Firebase Firestore

import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  getDocsFromServer,
  addDoc,
  setDoc,
  updateDoc,
  deleteField,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  runTransaction,
  writeBatch,
  QueryConstraint,
  onSnapshot,
  Unsubscribe,
  startAfter,
  Query,
  QuerySnapshot,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { getFirestoreDB } from './firebase';
import { sanitizeOrderItemsForFirestore } from './orderPersistence';
import { publicOrderingSchedule } from './businessHours';
import { normalizeOrderRecord, reconcileOrderItemsFromSnapshots, finalizeOrderDocumentForWrite, pickLongerOrderSnapshot, assertOrderWriteIntegrity, isIntentionalOrderAmendment, orderMeaningfulItemQty, coerceOrderItemsArray, computeOrderLiveEligible, mergeLastSentSnapshotForRunningAdd, ORDER_LIVE_UNPAID_MAX_AGE_MS } from './orderUtils';
import { ruleFromSavedExpenseLine } from './bankExpenseCategorization';
import { liveDataStatus, type FeedController } from './liveDataStatus';
import { fetchAuditLogsFromVps, fetchReportsDailyFromVps } from './vpsApi';
import { orderCreatedAtMs, toMillis } from './orderTimestamps';
import { mergeRegisterSessionRecords, normalizeRegisterSession } from './registerSessionUtils';
import {
  bookingDocId,
  dayKeysForWeek,
  dayMenuAsText,
  emptyWeekMenu,
  offeredPeriods,
  nextWeekId,
  nzDateKey,
  weekBoundsMs,
  weekIdContaining,
  isoWeekdayFromDayKey,
} from './mealPlanWeek';
import type {
  Order,
  Staff,
  Customer,
  LoyaltyReward,
  AppOffer,
  AppAnnouncement,
  MealPlanType,
  MealPlanTcVersion,
  MealPlanSubscription,
  MealPlanWeek,
  WeeklyBooking,
  MealPlanExceptionRequest,
  MealPlanCostSettings,
  MealPlanProduct,
  MealPlanEntitlement,
  MealPeriod,
  MealPlanDayMenu,
  MealPlanBookingMode,
  MenuItem,
  Category,
  InventoryItem,
  RestaurantAsset,
  Table,
  Reservation,
  Event,
  RegisterSession,
  QuickAddCombo,
  FloorFixture,
  WeeklyRoster,
  Shift,
  TimesheetEntry,
  OverseasShipment,
  ShipmentItem,
  Supplier,
  SupplierCatalog,
  PurchaseOrder,
  BillingClient,
  SupplierInvoice,
  ClientInvoice,
  PublicOnlineOrderingConfig,
  BusinessHours,
  ManualSalesOverride,
  AuditLogEntry,
  DailyExpense,
  PayslipRecord,
  Recipe,
  Coupon,
  CouponCampaign,
  CouponUsageLog,
  CouponRedemption,
  MonthlyReportDayEntry,
  MonthlyReportImportTransaction,
  MonthlyReportExpenseLine,
  BankExpenseCategorizationRule,
  PayrollPeriodImport,
  ReportsDailySummary,
  ReportsMonthlySummary,
  ReportsYearlySummary,
} from '../types';

/**
 * Firebase Firestore Database Service Layer
 * Provides CRUD operations for all collections
 */

// Lazy initialization - only get DB when actually needed
function getDB() {
  try {
    return getFirestoreDB();
  } catch (error) {
    console.error('❌ Failed to get Firestore DB:', error);
    throw error;
  }
}

// ============ Reconnecting subscription helper ============

/**
 * Wraps Firestore `onSnapshot` with automatic reconnect-on-error and
 * tab-visibility-aware refresh. Reports status to `liveDataStatus` so the
 * "Live / Reconnecting..." badge in the UI reflects connection health.
 *
 * Backoff: 1s, 2s, 4s, 8s, 16s, capped at 30s. Resets to 1s on the first
 * successful snapshot after a failure.
 *
 * The "offline" state kicks in after the third consecutive failure (~14s of
 * downtime) so a brief blip doesn't show staff a red dot for no reason.
 */
export interface ReconnectingSubscription {
  /** Stops the subscription permanently and removes it from the status registry. */
  unsubscribe: () => void;
  /** Forces an immediate re-subscribe. Used by visibility/online listeners and the manual refresh button. */
  refresh: () => void;
  /** True iff the underlying onSnapshot is currently delivering data. */
  isConnected: () => boolean;
}

export interface ReconnectingSubscriptionOptions<T> {
  /** Stable name for the feed - used as the registry key and shown in the badge tooltip. */
  name: string;
  /** Builds the Firestore query. Called once per (re)subscribe so callers can capture fresh state. */
  buildQuery: () => Query;
  /** Converts a single doc snapshot into the consumer type. */
  parseDoc: (doc: QueryDocumentSnapshot<DocumentData>) => T;
  /** Optional post-parse filter (e.g. exclude soft-deleted rows). */
  filter?: (item: T) => boolean;
  /** Optional in-memory sort applied before invoking `callback`. */
  sort?: (a: T, b: T) => number;
  /** Receives the parsed/filtered/sorted list on each snapshot. */
  callback: (items: T[]) => void;
}

const MIN_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
const OFFLINE_AFTER_FAILURES = 3;

export function createReconnectingSubscription<T>(opts: ReconnectingSubscriptionOptions<T>): ReconnectingSubscription {
  let innerUnsub: Unsubscribe | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryDelayMs = MIN_BACKOFF_MS;
  let consecutiveFailures = 0;
  let alive = true;
  let connected = false;

  const controller: FeedController = {
    refresh: () => {
      if (!alive) return;
      // Cancel any pending retry and immediately re-subscribe.
      if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
      if (innerUnsub) { innerUnsub(); innerUnsub = null; }
      retryDelayMs = MIN_BACKOFF_MS;
      consecutiveFailures = 0;
      // Mark as 'connecting' for the brief reattach window.
      liveDataStatus.setStatus(opts.name, { state: 'connecting' }, controller);
      start();
    },
  };

  const start = (): void => {
    if (!alive) return;
    try {
      const q = opts.buildQuery();
      innerUnsub = onSnapshot(
        q,
        (snap: QuerySnapshot<DocumentData>) => {
          consecutiveFailures = 0;
          retryDelayMs = MIN_BACKOFF_MS;
          connected = true;
          let items = snap.docs.map(opts.parseDoc);
          if (opts.filter) items = items.filter(opts.filter);
          if (opts.sort) items = items.sort(opts.sort);
          try {
            if (import.meta.env.DEV) {
              console.info(`[firestore-read] ${opts.name} snapshot: ${items.length} docs`);
            }
            opts.callback(items);
          } catch (cbErr) {
            console.error(`[reconnectingSub:${opts.name}] callback threw`, cbErr);
          }
          liveDataStatus.markSnapshot(opts.name, controller);
        },
        (err) => {
          connected = false;
          consecutiveFailures++;
          const errorCode = (err as any)?.code as string | undefined;
          const isOffline = consecutiveFailures >= OFFLINE_AFTER_FAILURES;
          liveDataStatus.setStatus(opts.name, {
            state: isOffline ? 'offline' : 'reconnecting',
            errorCode,
            retryDelayMs,
          }, controller);
          console.warn(
            `[reconnectingSub:${opts.name}] error (${errorCode ?? 'unknown'}), reconnecting in ${retryDelayMs}ms (failure #${consecutiveFailures})`,
            err
          );
          if (innerUnsub) { innerUnsub(); innerUnsub = null; }
          retryTimer = setTimeout(() => {
            retryTimer = null;
            retryDelayMs = Math.min(retryDelayMs * 2, MAX_BACKOFF_MS);
            start();
          }, retryDelayMs);
        }
      );
      // Optimistically mark as connecting until the first snapshot arrives.
      // markSnapshot in the success path will flip it to 'connected'.
      if (!connected) {
        liveDataStatus.setStatus(opts.name, { state: 'connecting' }, controller);
      }
    } catch (startErr) {
      connected = false;
      consecutiveFailures++;
      const errorCode = (startErr as any)?.code as string | undefined;
      const isOffline = consecutiveFailures >= OFFLINE_AFTER_FAILURES;
      liveDataStatus.setStatus(opts.name, {
        state: isOffline ? 'offline' : 'reconnecting',
        errorCode,
        retryDelayMs,
      }, controller);
      console.error(`[reconnectingSub:${opts.name}] failed to start, retrying in ${retryDelayMs}ms`, startErr);
      retryTimer = setTimeout(() => {
        retryTimer = null;
        retryDelayMs = Math.min(retryDelayMs * 2, MAX_BACKOFF_MS);
        start();
      }, retryDelayMs);
    }
  };

  // Register the feed with status 'connecting' immediately so the badge picks it up.
  liveDataStatus.setStatus(opts.name, { state: 'connecting' }, controller);
  start();

  return {
    unsubscribe: () => {
      alive = false;
      if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
      if (innerUnsub) { innerUnsub(); innerUnsub = null; }
      liveDataStatus.unregister(opts.name);
    },
    refresh: () => controller.refresh(),
    isConnected: () => connected,
  };
}

// ============ Generic Helpers ============

async function getAll<T>(collectionName: string, ...constraints: QueryConstraint[]): Promise<T[]> {
  try {
    console.log(`📦 getAll called for collection: ${collectionName}, constraints: ${constraints.length}`);
    const db = getDB();
    const collectionRef = collection(db, collectionName);
    const q = constraints.length > 0 ? query(collectionRef, ...constraints) : collectionRef;
    const snapshot = await getDocs(q);
    const results = snapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id } as T));
    console.log(`✅ getAll result for ${collectionName}: ${results.length} documents`);
    return results;
  } catch (error) {
    console.error(`❌ getAll error for ${collectionName}:`, error);
    throw error;
  }
}

async function getById<T>(collectionName: string, id: string): Promise<T | null> {
  const db = getDB();
    const docRef = doc(db, collectionName, id);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? ({ ...docSnap.data(), id: docSnap.id } as T) : null;
}

// Helper function to remove undefined values from an object (Firestore doesn't accept undefined)
// Recursively removes undefined values from nested objects and arrays
function removeUndefined<T extends Record<string, any>>(obj: T): Partial<T> {
  const cleaned: Partial<T> = {};
  for (const key in obj) {
    const value = obj[key];
    if (value === undefined) {
      // Skip undefined values
      continue;
    } else if (Array.isArray(value)) {
      // Clean array items recursively
      cleaned[key] = value.map(item => 
        typeof item === 'object' && item !== null ? removeUndefined(item) : item
      ) as any;
    } else if (typeof value === 'object' && value !== null) {
      // Clean nested objects recursively
      cleaned[key] = removeUndefined(value) as any;
    } else {
      // Primitive value, keep it
      cleaned[key] = value;
    }
  }
  return cleaned;
}

async function create<T>(collectionName: string, data: Omit<T, 'id'>): Promise<T> {
  const db = getDB();
    const collectionRef = collection(db, collectionName);
  // Remove undefined values before saving to Firestore
  const cleanedData = removeUndefined(data as Record<string, any>);
  const docRef = await addDoc(collectionRef, cleanedData);
  const docSnap = await getDoc(docRef);
  return { ...docSnap.data(), id: docRef.id } as T;
}

async function createWithId<T>(collectionName: string, id: string, data: Omit<T, 'id'>): Promise<T> {
  const db = getDB();
    const docRef = doc(db, collectionName, id);
  // Remove undefined values before saving to Firestore
  const cleanedData = removeUndefined(data as Record<string, any>);
  await setDoc(docRef, cleanedData);
  const docSnap = await getDoc(docRef);
  return { ...docSnap.data(), id: docRef.id } as T;
}

// Export createWithId for use in other modules
export { createWithId };

/**
 * Create a brand-new order document with a guaranteed-unique id.
 *
 * generateOrderId() derives its sequence from `timestamp % 10000`, which only has ~10,000
 * distinct values per 10-second window. Two terminals placing new orders around the same
 * moment (very plausible during a rush) can generate the SAME id. Because order creation
 * used to call `setDoc` blindly, a colliding write would silently overwrite an unrelated
 * order for a different table — which is exactly what caused items from one table to show
 * up mixed into another table's order.
 *
 * This helper checks-and-creates inside a transaction and regenerates a fresh id (new
 * timestamp) on collision, instead of ever overwriting an existing order document.
 */
async function createOrderWithUniqueId(
  collectionName: string,
  buildData: (id: string) => Record<string, any>,
  maxAttempts = 5,
  preferredId?: string,
): Promise<{ id: string; data: DocumentData; prepared: Record<string, any> }> {
  const db = getDB();
  const { generateOrderId } = await import('./orderIdGenerator');
  let lastId = '';
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const id =
      attempt === 0 && preferredId && preferredId.trim()
        ? preferredId.trim()
        : generateOrderId();
    lastId = id;
    const docRef = doc(db, collectionName, id);
    const prepared = buildData(id);
    try {
      await runTransaction(db, async (tx) => {
        const existing = await tx.get(docRef);
        if (existing.exists()) {
          throw new Error('ORDER_ID_COLLISION');
        }
        tx.set(docRef, removeUndefined(prepared));
      });
      const docSnap = await getDoc(docRef);
      return { id, data: (docSnap.data() ?? {}) as DocumentData, prepared };
    } catch (err: any) {
      if (err?.message === 'ORDER_ID_COLLISION' && attempt < maxAttempts - 1) {
        console.warn(`[orders] Order id collision on ${id}, regenerating (attempt ${attempt + 1})`);
        continue;
      }
      throw err;
    }
  }
  throw new Error(`Failed to create order: exhausted id collision retries (last tried ${lastId})`);
}

/** Prefer prepared client payload when Firestore read-back is thin/empty after create. */
export function mergePreparedOrderReadback(
  prepared: Omit<Order, 'id'> | Record<string, any>,
  data: DocumentData | Record<string, any> | undefined,
  id: string,
): Order {
  const server = (data ?? {}) as Record<string, any>;
  const preparedItems = Array.isArray(prepared.items) ? prepared.items : [];
  const serverItems = Array.isArray(server.items) ? server.items : [];
  const items = preparedItems.length > 0 && serverItems.length === 0 ? preparedItems : (serverItems.length > 0 ? serverItems : preparedItems);
  const preparedTotal = typeof prepared.totalAmount === 'number' ? prepared.totalAmount : 0;
  const serverTotal = typeof server.totalAmount === 'number' ? server.totalAmount : 0;
  // Thin create read-back: prefer prepared when server omitted money. Do not Math.max when
  // prepared already applied a discount (lower total is intentional).
  const preparedHasDiscount =
    Number((prepared as Order).discountAmount ?? 0) > 0.005 ||
    Number((prepared as Order).couponDiscountAmount ?? 0) > 0.005 ||
    Number((prepared as Order).manualDiscount ?? 0) > 0.005 ||
    Number((prepared as Order).firstTableDiscountAmount ?? 0) > 0.005 ||
    !!(prepared as Order).appliedReward;
  const totalAmount = preparedHasDiscount
    ? (preparedTotal > 0 ? preparedTotal : serverTotal)
    : Math.max(preparedTotal, serverTotal);
  const merged: Order = {
    ...(prepared as Omit<Order, 'id'>),
    ...server,
    id,
    items,
    totalAmount,
    subtotal: typeof server.subtotal === 'number' ? server.subtotal : (prepared as Order).subtotal,
    taxAmount: typeof server.taxAmount === 'number' ? server.taxAmount : (prepared as Order).taxAmount,
  } as Order;
  if (!preparedHasDiscount && (merged.totalAmount ?? 0) < preparedTotal) {
    merged.totalAmount = preparedTotal;
  }
  // Prefer prepared buffet package flags when server omitted them on a thin read-back.
  if (preparedItems.some((i: any) => i?.isBuffetPackage) && !items.some((i: any) => i?.isBuffetPackage)) {
    merged.items = preparedItems as Order['items'];
  }
  return normalizeOrderRecord(merged);
}

/** Reject stale client snapshots that drop running-order lines without an explicit removal. */
function preventStaleOrderItemsShrink(currentItems: any[], proposedItems: any[]): any[] {
  if (!Array.isArray(proposedItems)) return proposedItems;
  // Empty items on an existing order is almost always cache/truncation — reject unless already empty.
  if (proposedItems.length === 0) {
    if (Array.isArray(currentItems) && currentItems.length > 0) {
      console.warn(`[orders] Blocked empty items overwrite (${currentItems.length} -> 0 lines)`);
      return currentItems;
    }
    return proposedItems;
  }
  if (!Array.isArray(currentItems) || currentItems.length === 0) return proposedItems;
  if (proposedItems.length >= currentItems.length) return proposedItems;

  const lineKey = (item: any, idx: number) =>
    item?.lineId ?? `${item?.id ?? ''}:${item?.selectedMeatOption ?? ''}:${idx}`;

  const currentKeys = new Set(currentItems.map((item, idx) => lineKey(item, idx)));
  const proposedKeys = new Set(proposedItems.map((item, idx) => lineKey(item, idx)));
  const missingKeys = [...currentKeys].filter((key) => !proposedKeys.has(key));
  if (missingKeys.length === 0) return proposedItems;

  const allExplicitlyRemoved = missingKeys.every((key) => {
    const idx = currentItems.findIndex((item, i) => lineKey(item, i) === key);
    return idx >= 0 && currentItems[idx]?.removed === true;
  });
  if (allExplicitlyRemoved) return proposedItems;

  console.warn(
    `[orders] Blocked stale items shrink (${currentItems.length} -> ${proposedItems.length} lines)`,
  );
  return currentItems;
}

/** Merge a partial order update with server truth and expand truncated line lists. */
function buildOrderDocumentForWrite(
  currentData: Record<string, any>,
  cleanedUpdates: Record<string, any>,
): Record<string, any> {
  const currentItems = Array.isArray(currentData.items) ? currentData.items : [];
  const sanitizedProposed = sanitizeOrderItemsForFirestore(cleanedUpdates.items as any[]);
  const newItems = preventStaleOrderItemsShrink(currentItems, sanitizedProposed);

  const mergedData: Record<string, any> = {
    ...currentData,
    ...cleanedUpdates,
    items: newItems,
  };

  const snapshotForRepair =
    (Array.isArray(cleanedUpdates.lastSentItemSnapshot) && cleanedUpdates.lastSentItemSnapshot.length > 0
      ? cleanedUpdates.lastSentItemSnapshot
      : currentData.lastSentItemSnapshot) ?? [];
  if (snapshotForRepair.length > newItems.length) {
    const repairedItems = reconcileOrderItemsFromSnapshots({
      ...(mergedData as Order),
      lastSentItemSnapshot: snapshotForRepair,
    });
    if (repairedItems.length > newItems.length) {
      mergedData.items = sanitizeOrderItemsForFirestore(repairedItems);
      console.warn(
        `[orders] Expanded truncated items before save (${newItems.length} -> ${repairedItems.length})`,
      );
    }
  }

  if ((!mergedData.orderBatches || !Array.isArray(mergedData.orderBatches) || mergedData.orderBatches.length === 0) &&
      Array.isArray(currentData.orderBatches) && currentData.orderBatches.length > 0) {
    mergedData.orderBatches = currentData.orderBatches;
  }

  const asCurrent = { ...currentData, id: currentData.id ?? 'tmp' } as Order;
  const asProposed = { ...mergedData, id: mergedData.id ?? asCurrent.id } as Order;
  const intentionalAmendment = isIntentionalOrderAmendment(asCurrent, asProposed);

  if (intentionalAmendment && Array.isArray(cleanedUpdates.lastSentItemSnapshot)) {
    mergedData.lastSentItemSnapshot = cleanedUpdates.lastSentItemSnapshot;
  } else if (
    Array.isArray(cleanedUpdates.lastSentItemSnapshot) &&
    (orderMeaningfulItemQty(asProposed) > orderMeaningfulItemQty(asCurrent) + 0.001 ||
      coerceOrderItemsArray(asProposed.items).filter((i) => !i.removed).length >
        coerceOrderItemsArray(asCurrent.items).filter((i) => !i.removed).length)
  ) {
    // Running-order additions: keep the pre-update baseline so kitchen print/KDS can diff NEW lines.
    // pickLonger would otherwise resurrect a fuller snapshot and hide the delta.
    // If that baseline is shorter only because the stored snapshot still lists a
    // removed/package/discount row, keep it. Reattach any live kitchen line the
    // rebuild dropped so the integrity check does not block the add.
    mergedData.lastSentItemSnapshot = mergeLastSentSnapshotForRunningAdd(
      currentData.lastSentItemSnapshot,
      cleanedUpdates.lastSentItemSnapshot,
      currentItems,
    );
  } else {
    mergedData.lastSentItemSnapshot = pickLongerOrderSnapshot(
      currentData.lastSentItemSnapshot,
      pickLongerOrderSnapshot(cleanedUpdates.lastSentItemSnapshot, mergedData.lastSentItemSnapshot),
    ) ?? mergedData.lastSentItemSnapshot ?? currentData.lastSentItemSnapshot;
  }

  mergedData.initialKitchenSnapshot = pickLongerOrderSnapshot(
    currentData.initialKitchenSnapshot,
    pickLongerOrderSnapshot(cleanedUpdates.initialKitchenSnapshot, mergedData.initialKitchenSnapshot),
  ) ?? mergedData.initialKitchenSnapshot ?? currentData.initialKitchenSnapshot;

  return removeUndefined(finalizeOrderDocumentForWrite(mergedData as Order));
}

/** Apply snapshot-only order patches without shrinking kitchen history. */
function buildOrderSnapshotPatchForWrite(
  currentData: Record<string, any>,
  cleanedUpdates: Record<string, any>,
): Record<string, any> {
  const patch: Record<string, any> = { ...cleanedUpdates };

  if ('lastSentItemSnapshot' in cleanedUpdates) {
    patch.lastSentItemSnapshot = pickLongerOrderSnapshot(
      currentData.lastSentItemSnapshot,
      cleanedUpdates.lastSentItemSnapshot,
    ) ?? currentData.lastSentItemSnapshot;
  }

  if ('initialKitchenSnapshot' in cleanedUpdates) {
    patch.initialKitchenSnapshot = pickLongerOrderSnapshot(
      currentData.initialKitchenSnapshot,
      cleanedUpdates.initialKitchenSnapshot,
    ) ?? currentData.initialKitchenSnapshot;
  }

  return removeUndefined(patch);
}

async function update<T>(collectionName: string, id: string, updates: Partial<T>): Promise<T> {
  const db = getDB();
    const docRef = doc(db, collectionName, id);
  // Remove undefined values before updating Firestore
  const cleanedUpdates = removeUndefined(updates as Record<string, any>);
  
  // Log order updates for debugging
  if (collectionName === 'orders' && cleanedUpdates.items) {
    console.log(`💾 Saving order ${id} to Firestore with ${cleanedUpdates.items.length} items`);
    console.log(`📦 Items being saved:`, cleanedUpdates.items.map((item: any) => ({
      id: item.id,
      name: item.name,
      status: item.status,
      served: item.served,
      quantity: item.quantity
    })));
  }
  
  // For orders with items array, we need to ensure the items array is completely replaced
  // Firestore's updateDoc can have issues with array updates, so we use a different approach
  if (collectionName === 'orders' && cleanedUpdates.items) {
    const completeData = await runTransaction(db, async (transaction) => {
      const currentDoc = await transaction.get(docRef);
      const currentData = currentDoc.exists() ? (currentDoc.data() as Record<string, any>) : {};
      const merged = buildOrderDocumentForWrite(currentData, cleanedUpdates);
      const serverOrder = currentDoc.exists()
        ? ({ ...currentData, id } as Order)
        : undefined;
      assertOrderWriteIntegrity(serverOrder, { ...merged, id } as Order);
      transaction.set(docRef, merged);
      return merged;
    });

    console.log(`🔄 Transaction write for order ${id} — items count: ${completeData.items?.length ?? 0}`);
  } else if (
    collectionName === 'orders' &&
    (cleanedUpdates.lastSentItemSnapshot || cleanedUpdates.initialKitchenSnapshot)
  ) {
    await runTransaction(db, async (transaction) => {
      const currentDoc = await transaction.get(docRef);
      if (!currentDoc.exists()) {
        const liveEligible = computeOrderLiveEligible({
          ...(cleanedUpdates as Order),
          id,
        } as Order);
        transaction.set(docRef, { ...cleanedUpdates, liveEligible }, { merge: true });
        return;
      }
      const currentData = currentDoc.data() as Record<string, any>;
      const patch = buildOrderSnapshotPatchForWrite(currentData, cleanedUpdates);
      const serverOrder = { ...currentData, id } as Order;
      const nextOrder = { ...serverOrder, ...patch } as Order;
      nextOrder.liveEligible = computeOrderLiveEligible(nextOrder);
      patch.liveEligible = nextOrder.liveEligible;
      assertOrderWriteIntegrity(serverOrder, nextOrder);
      transaction.update(docRef, patch);
    });
  } else if (collectionName === 'orders') {
    await runTransaction(db, async (transaction) => {
      const currentDoc = await transaction.get(docRef);
      if (!currentDoc.exists()) {
        const liveEligible = computeOrderLiveEligible({
          ...(cleanedUpdates as Order),
          id,
        } as Order);
        transaction.set(docRef, { ...cleanedUpdates, liveEligible }, { merge: true });
        return;
      }
      const currentData = currentDoc.data() as Record<string, any>;
      const nextOrder = { ...currentData, ...cleanedUpdates, id } as Order;
      const liveEligible = computeOrderLiveEligible(nextOrder);
      assertOrderWriteIntegrity(
        { ...currentData, id } as Order,
        { ...nextOrder, liveEligible },
      );
      transaction.update(docRef, { ...cleanedUpdates, liveEligible });
    });
  } else {
    // For other collections or updates without items, use updateDoc
    await updateDoc(docRef, cleanedUpdates);
  }
  
  const docSnap = await getDoc(docRef);
  const rawData = docSnap.data();
  const savedData = { ...rawData, id: docRef.id } as T;
  
  // Log what was actually saved
  if (collectionName === 'orders') {
    console.log(`✅ Order ${id} saved to Firestore`);
    if (rawData?.items) {
      console.log(`📦 Saved items count: ${rawData.items.length}`);
      console.log(`📦 Saved items:`, rawData.items.map((item: any) => ({
        id: item.id,
        name: item.name,
        status: item.status,
        served: item.served,
        quantity: item.quantity
      })));
      console.log(`🔍 Raw Firestore data - items array length:`, rawData.items.length);
      console.log(`🔍 Raw Firestore data - items type:`, Array.isArray(rawData.items) ? 'array' : typeof rawData.items);
    } else {
      console.warn(`⚠️ Order ${id} saved but items array is missing or undefined!`);
      console.log(`🔍 Raw Firestore data keys:`, Object.keys(rawData || {}));
    }
  }
  
  return savedData;
}

async function remove(collectionName: string, id: string): Promise<void> {
  const db = getDB();
    const docRef = doc(db, collectionName, id);
  await deleteDoc(docRef);
}

// ============ Orders ============

/** Live POS/KDS listener — default is the proven 14-day timestamp window.
 * Set VITE_ORDERS_LIVE_ELIGIBLE=true only after dig/prod backfill + Firestore index Ready.
 * Membership field is still written on every order save for a later cutover. */
const ORDERS_LIVE_LISTENER_FALLBACK_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

function useOrdersLiveEligibleListener(): boolean {
  try {
    const raw = String((import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_ORDERS_LIVE_ELIGIBLE ?? 'false');
    return raw === 'true' || raw === '1';
  } catch {
    return false;
  }
}

function getOrdersLiveCutoffMs(): number {
  return Date.now() - ORDERS_LIVE_LISTENER_FALLBACK_MAX_AGE_MS;
}

function getOrdersUnpaidFetchCutoffMs(): number {
  return Date.now() - ORDER_LIVE_UNPAID_MAX_AGE_MS;
}

function parseOrderDoc(snap: QueryDocumentSnapshot<DocumentData>): Order {
  return normalizeOrderRecord({ ...snap.data(), id: snap.id } as Order);
}

async function fetchOrderQueryDocs(
  q: Query,
  options?: { preferServer?: boolean },
): Promise<Order[]> {
  const snapshot = options?.preferServer ? await getDocsFromServer(q) : await getDocs(q);
  return snapshot.docs.map((d) => parseOrderDoc(d)).filter((o) => !o.isDeleted);
}

function filterLiveOrder(_order: Order): boolean {
  // Include soft-deleted docs so clients receive tombstones and drop local rows.
  return true;
}

function orderTimestampMs(order: Order): number {
  return orderCreatedAtMs(order);
}

function mergeLiveOrdersById(...lists: Order[][]): Order[] {
  const map = new Map<string, Order>();
  for (const list of lists) {
    for (const order of list) {
      if (!order.isDeleted) {
        map.set(order.id, order);
      }
    }
  }
  return Array.from(map.values()).sort((a, b) => orderTimestampMs(b) - orderTimestampMs(a));
}

export const ordersService = {
  async getAll(): Promise<Order[]> {
    const all = await getAll<Order>('orders', orderBy('timestamp', 'desc'));
    // Exclude soft-deleted orders from normal views
    return all.filter((o) => !o.isDeleted);
  },

  /** Unpaid tickets for POS Pending — one-shot only (not a live listener). Longer than recent window. */
  async getOpenUnpaid(options?: { preferServer?: boolean }): Promise<Order[]> {
    const cutoff = getOrdersUnpaidFetchCutoffMs();
    return fetchOrderQueryDocs(
      query(
        collection(getDB(), 'orders'),
        where('paymentStatus', '==', 'unpaid'),
        where('timestamp', '>=', cutoff),
        orderBy('timestamp', 'desc'),
      ),
      options,
    );
  },

  /** Same membership as the live listener — use for refresh instead of unbounded getAll(). */
  async getRecent(options?: { preferServer?: boolean }): Promise<Order[]> {
    if (useOrdersLiveEligibleListener()) {
      return fetchOrderQueryDocs(
        query(
          collection(getDB(), 'orders'),
          where('liveEligible', '==', true),
          orderBy('timestamp', 'desc'),
        ),
        options,
      );
    }
    const cutoff = getOrdersLiveCutoffMs();
    return fetchOrderQueryDocs(
      query(
        collection(getDB(), 'orders'),
        where('timestamp', '>=', cutoff),
        orderBy('timestamp', 'desc'),
      ),
      options,
    );
  },

  async getByDateRange(
    start: number,
    end: number,
    options?: { preferServer?: boolean },
  ): Promise<Order[]> {
    return fetchOrderQueryDocs(
      query(
        collection(getDB(), 'orders'),
        orderBy('timestamp', 'asc'),
        where('timestamp', '>=', start),
        where('timestamp', '<=', end),
      ),
      options,
    );
  },

  async getByDateRangePage(
    start: number,
    end: number,
    options?: { pageSize?: number; cursorTimestamp?: number; cursorId?: string },
  ): Promise<{ rows: Order[]; nextCursor: { timestamp: number; id: string } | null }> {
    const db = getDB();
    const pageSize = Math.min(50, Math.max(1, options?.pageSize ?? 25));
    const constraints: QueryConstraint[] = [
      where('timestamp', '>=', start),
      where('timestamp', '<=', end),
      orderBy('timestamp', 'asc'),
      orderBy('__name__', 'asc'),
    ];
    if (typeof options?.cursorTimestamp === 'number' && options.cursorId) {
      constraints.push(startAfter(options.cursorTimestamp, options.cursorId));
    }
    constraints.push(limit(pageSize));
    const q = query(collection(db, 'orders'), ...constraints);
    const snapshot = await getDocs(q);
    const rows = snapshot.docs
      .map((d) => ({ ...d.data(), id: d.id } as Order))
      .filter((o) => !o.isDeleted);
    const last = snapshot.docs[snapshot.docs.length - 1];
    const nextCursor = last
      ? { timestamp: Number(last.get('timestamp') || 0), id: last.id }
      : null;
    return { rows, nextCursor };
  },

  async getById(id: string, options?: { preferServer?: boolean }): Promise<Order | null> {
    const db = getDB();
    const docRef = doc(db, 'orders', id);
    const docSnap = options?.preferServer ? await getDocFromServer(docRef) : await getDoc(docRef);
    if (!docSnap.exists()) return null;
    const order = normalizeOrderRecord({ ...docSnap.data(), id: docSnap.id } as Order);
    return order.isDeleted ? null : order;
  },

  async create(order: Omit<Order, 'id'>, options?: { preferredId?: string }): Promise<Order> {
    const withEligible = {
      ...order,
      items: sanitizeOrderItemsForFirestore(order.items ?? []),
      liveEligible: computeOrderLiveEligible(order as Order),
    };
    // Generate a unique invoice-style order ID (INV-YYYYMMDD-XXXX), retrying on the rare
    // chance of a collision with an existing order (see createOrderWithUniqueId above) —
    // never blindly overwrite another table's order document.
    // preferredId lets buffet UI open payment before the write settles.
    const { id, data, prepared: written } = await createOrderWithUniqueId(
      'orders',
      () => withEligible as Record<string, any>,
      5,
      options?.preferredId,
    );
    // Merge prepared over thin getDoc read-back so package lines / totals never vanish.
    return mergePreparedOrderReadback(written, data, id);
  },

  async update(id: string, updates: Partial<Order>): Promise<Order> {
    return update<Order>('orders', id, updates);
  },

  async delete(id: string): Promise<void> {
    // Soft delete: keep document for audit/history, hide from UI/reports
    const deletedAt = Date.now();
    await update<Order>('orders', id, {
      isDeleted: true,
      deletedAt,
      liveEligible: computeOrderLiveEligible({
        status: 'cancelled',
        paymentStatus: 'unpaid',
        timestamp: deletedAt,
        isDeleted: true,
        deletedAt,
      }),
    });
  },

  // Real-time subscription — single liveEligible query (auto-reconnecting).
  subscribe(callback: (orders: Order[]) => void): Unsubscribe {
    const sub = createReconnectingSubscription<Order>({
      name: 'orders',
      buildQuery: () => {
        const db = getDB();
        if (useOrdersLiveEligibleListener()) {
          return query(
            collection(db, 'orders'),
            where('liveEligible', '==', true),
            orderBy('timestamp', 'desc'),
          );
        }
        const cutoff = getOrdersLiveCutoffMs();
        return query(
          collection(db, 'orders'),
          where('timestamp', '>=', cutoff),
          orderBy('timestamp', 'desc'),
        );
      },
      parseDoc: (snap) => parseOrderDoc(snap),
      filter: filterLiveOrder,
      callback: (items) => {
        callback(items);
      },
    });

    return () => {
      sub.unsubscribe();
    };
  },
};

async function getReportsDailyFromFirestore(
  dateFrom: string,
  dateTo: string,
): Promise<ReportsDailySummary[]> {
  try {
    const db = getDB();
    const q = query(
      collection(db, 'reports_daily'),
      where('date', '>=', dateFrom),
      where('date', '<=', dateTo),
      orderBy('date', 'asc'),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as ReportsDailySummary));
  } catch (error: any) {
    if (error?.code === 'permission-denied') {
      return [];
    }
    throw error;
  }
}

export const reportsDailySummaryService = {
  async getByDateRange(dateFrom: string, dateTo: string): Promise<ReportsDailySummary[]> {
    const fromVps = await fetchReportsDailyFromVps(dateFrom, dateTo);
    if (fromVps != null) {
      return fromVps;
    }
    return getReportsDailyFromFirestore(dateFrom, dateTo);
  },

  async getByDate(date: string): Promise<ReportsDailySummary | null> {
    const fromVps = await fetchReportsDailyFromVps(date, date);
    if (fromVps != null) {
      return fromVps[0] ?? null;
    }
    return getById<ReportsDailySummary>('reports_daily', date);
  },
};

export const reportsMonthlySummaryService = {
  async getByMonth(month: string): Promise<ReportsMonthlySummary | null> {
    return getById<ReportsMonthlySummary>('reports_monthly', month);
  },
};

export const reportsYearlySummaryService = {
  async getByYear(year: string): Promise<ReportsYearlySummary | null> {
    return getById<ReportsYearlySummary>('reports_yearly', year);
  },
};

// ============ Audit Logs ============

export const auditLogsService = {
  async add(entry: Omit<AuditLogEntry, 'id'>): Promise<AuditLogEntry> {
    const saved = await create<AuditLogEntry>('audit_logs', entry as AuditLogEntry);
    return saved;
  },

  async getByDateRange(start: number, end: number): Promise<AuditLogEntry[]> {
    const fromVps = await fetchAuditLogsFromVps(start, end);
    if (fromVps != null) {
      return fromVps;
    }
    const constraints: QueryConstraint[] = [
      where('timestamp', '>=', start),
      where('timestamp', '<=', end),
      orderBy('timestamp', 'desc'),
    ];
    return getAll<AuditLogEntry>('audit_logs', ...constraints);
  },
};

// ============ Daily Expenses (GST/tax reconciliation) ============

export const dailyExpensesService = {
  async create(expense: Omit<DailyExpense, 'id'>): Promise<DailyExpense> {
    return create<DailyExpense>('daily_expenses', expense as DailyExpense);
  },

  async update(id: string, updates: Partial<Omit<DailyExpense, 'id'>>): Promise<DailyExpense> {
    return update<DailyExpense>('daily_expenses', id, updates);
  },

  async remove(id: string): Promise<void> {
    const db = getDB();
    const docRef = doc(db, 'daily_expenses', id);
    await deleteDoc(docRef);
  },

  async getByDateRange(dateFrom: string, dateTo: string): Promise<DailyExpense[]> {
    const constraints: QueryConstraint[] = [
      where('date', '>=', dateFrom),
      where('date', '<=', dateTo),
      orderBy('date', 'desc'),
    ];
    const list = await getAll<DailyExpense>('daily_expenses', ...constraints);
    list.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
    return list;
  },
};

// ============ Monthly Report Shared Import State ============

export const monthlyReportDayEntriesService = {
  async getByMonth(month: string): Promise<MonthlyReportDayEntry[]> {
    const constraints: QueryConstraint[] = [
      where('month', '==', month),
      orderBy('date', 'asc'),
    ];
    return getAll<MonthlyReportDayEntry>('monthly_report_day_entries', ...constraints);
  },

  async upsertMany(entries: Omit<MonthlyReportDayEntry, 'id'>[]): Promise<void> {
    if (entries.length === 0) return;
    const db = getDB();
    const chunkSize = 400;
    for (let i = 0; i < entries.length; i += chunkSize) {
      const chunk = entries.slice(i, i + chunkSize);
      const batch = writeBatch(db);
      chunk.forEach((entry) => {
        const id = `${entry.month}_${entry.date}`;
        const ref = doc(db, 'monthly_report_day_entries', id);
        const cleaned = removeUndefined(entry as Record<string, any>);
        batch.set(ref, cleaned, { merge: true });
      });
      await batch.commit();
    }
  },
};

export const monthlyReportImportTransactionsService = {
  async getByMonth(month: string): Promise<MonthlyReportImportTransaction[]> {
    const constraints: QueryConstraint[] = [
      where('month', '==', month),
      orderBy('date', 'asc'),
    ];
    return getAll<MonthlyReportImportTransaction>('monthly_report_import_transactions', ...constraints);
  },

  async createMany(transactions: Omit<MonthlyReportImportTransaction, 'id'>[], ids: string[]): Promise<void> {
    if (transactions.length === 0 || ids.length === 0) return;
    const db = getDB();
    const chunkSize = 400;
    const toDocId = (rawId: string): string => `txn_${encodeURIComponent(rawId)}`;
    for (let i = 0; i < transactions.length; i += chunkSize) {
      const txChunk = transactions.slice(i, i + chunkSize);
      const idChunk = ids.slice(i, i + chunkSize);
      const batch = writeBatch(db);
      txChunk.forEach((tx, index) => {
        const id = idChunk[index];
        const safeDocId = toDocId(id);
        const ref = doc(db, 'monthly_report_import_transactions', safeDocId);
        const cleaned = removeUndefined({ ...tx, id } as Record<string, any>);
        batch.set(ref, cleaned, { merge: false });
      });
      await batch.commit();
    }
  },
};

export const bankExpenseCategorizationRulesService = {
  async listAll(): Promise<BankExpenseCategorizationRule[]> {
    const db = getDB();
    const snapshot = await getDocs(collection(db, 'bank_expense_categorization_rules'));
    return snapshot.docs.map((d) => {
      const data = d.data() as Omit<BankExpenseCategorizationRule, 'id'>;
      return { id: d.id, ...data } as BankExpenseCategorizationRule;
    });
  },

  async upsertFromLine(line: MonthlyReportExpenseLine, meta: { updatedBy?: number; updatedByName?: string }): Promise<void> {
    const partial = ruleFromSavedExpenseLine(line);
    if (!partial.patternKey) return;
    const enc = new TextEncoder().encode(partial.patternKey);
    const buf = await crypto.subtle.digest('SHA-256', enc);
    const id = Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    const db = getDB();
    const data: BankExpenseCategorizationRule = {
      id,
      patternKey: partial.patternKey,
      sourceLabel: partial.sourceLabel,
      expenseCategory: partial.expenseCategory,
      expenseType: partial.expenseType,
      gstMode: partial.gstMode,
      updatedAt: Date.now(),
      updatedBy: meta.updatedBy,
      updatedByName: meta.updatedByName,
    };
    await setDoc(doc(db, 'bank_expense_categorization_rules', id), removeUndefined(data as Record<string, unknown>), {
      merge: true,
    });
  },
};

// ============ Payroll period imports (Detailed Reports reconciliation) ============

export const payrollPeriodImportsService = {
  async listRecent(maxResults: number = 80): Promise<PayrollPeriodImport[]> {
    const db = getDB();
    const q = query(
      collection(db, 'payroll_period_imports'),
      orderBy('importedAt', 'desc'),
      limit(maxResults)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as PayrollPeriodImport));
  },

  async getById(id: string): Promise<PayrollPeriodImport | null> {
    return getById<PayrollPeriodImport>('payroll_period_imports', id);
  },

  async create(data: Omit<PayrollPeriodImport, 'id'>): Promise<PayrollPeriodImport> {
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `pay-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    return createWithId<PayrollPeriodImport>('payroll_period_imports', id, data);
  },

  async remove(id: string): Promise<void> {
    const db = getDB();
    await deleteDoc(doc(db, 'payroll_period_imports', id));
  },
};

// ============ Categories ============

// Internal: ID/display-order normalisation for category docs. Used by both getAll
// and the real-time subscription so the two paths produce identical objects.
function normalizeCategoryDoc(raw: Category): Category {
  const data = raw as Category & Record<string, unknown>;
  return {
    ...raw,
    id: (typeof raw.id === 'string' && /^\d+$/.test(raw.id))
      ? parseInt(raw.id, 10)
      : (raw.id as number | string),
    ...normalizeMenuChannelFields(data),
  };
}

function compareCategories(a: Category, b: Category): number {
  const aOrder = (a as any).displayOrder;
  const bOrder = (b as any).displayOrder;
  if (aOrder !== undefined && bOrder !== undefined) return aOrder - bOrder;
  if (aOrder !== undefined) return -1;
  if (bOrder !== undefined) return 1;
  return (a.name || '').localeCompare(b.name || '');
}

export const categoriesService = {
  async getAll(): Promise<Category[]> {
    try {
      console.log('📂 Fetching categories from Firestore...');
      let result = await getAll<Category>('categories');
      result = result.map(normalizeCategoryDoc).sort(compareCategories);
      console.log('✅ Categories fetched and sorted:', result.length);
      return result;
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
      throw error;
    }
  },

  async getById(id: string | number): Promise<Category | null> {
    return getById<Category>('categories', String(id));
  },

  async create(category: Omit<Category, 'id'>): Promise<Category> {
    return create<Category>('categories', category);
  },

  async update(id: string | number, updates: Partial<Category>): Promise<Category> {
    return update<Category>('categories', String(id), updates);
  },

  async delete(id: string | number): Promise<void> {
    return remove('categories', String(id));
  },

  /**
   * Real-time subscription with auto-reconnect. Replaces the previous one-shot
   * fetch pattern in useCategories so a flaky network or a tab idle for hours
   * never leaves the sidebar empty.
   */
  subscribe(callback: (categories: Category[]) => void): Unsubscribe {
    const sub = createReconnectingSubscription<Category>({
      name: 'categories',
      buildQuery: () => query(collection(getDB(), 'categories')),
      parseDoc: (snap) => normalizeCategoryDoc({ id: snap.id, ...snap.data() } as Category),
      sort: compareCategories,
      callback,
    });
    return sub.unsubscribe;
  },
};

// ============ Menu Items ============
// Image URLs: Stored in Firestore (imageUrl, imageUrlThumb) when uploaded. Used directly
// in <img src> at display time. No getDownloadURL() at read = fastest UX, scales well.

// Internal: snake_case -> camelCase normalisation for menu_items docs.
// Shared by getAll, getById, and the real-time subscription so all reads
// produce identical shapes.
function readChannelFlag(data: Record<string, unknown>, camel: string, snake: string): boolean | undefined {
  if (data[camel] !== undefined) return data[camel] as boolean;
  if (data[snake] !== undefined) return data[snake] as boolean;
  return undefined;
}

function normalizeMenuChannelFields(data: Record<string, unknown>): {
  availableOnline?: boolean;
  availableQr?: boolean;
  availableDineIn?: boolean;
  availableTakeaway?: boolean;
  availableDelivery?: boolean;
  availableMealPlan?: boolean;
} {
  return {
    availableOnline: readChannelFlag(data, 'availableOnline', 'available_online'),
    availableQr: readChannelFlag(data, 'availableQr', 'available_qr'),
    availableDineIn: readChannelFlag(data, 'availableDineIn', 'available_dine_in'),
    availableTakeaway: readChannelFlag(data, 'availableTakeaway', 'available_takeaway'),
    availableDelivery: readChannelFlag(data, 'availableDelivery', 'available_delivery'),
    availableMealPlan: readChannelFlag(data, 'availableMealPlan', 'available_meal_plan'),
  };
}

function normalizeMenuItemDoc(raw: MenuItem | Record<string, unknown>): MenuItem {
  const data = raw as any;
  const rawCategoryId = data.category_id !== undefined ? data.category_id : data.categoryId;
  let categoryId: number | string;
  if (typeof rawCategoryId === 'number' && !Number.isNaN(rawCategoryId)) {
    categoryId = rawCategoryId;
  } else if (typeof rawCategoryId === 'string') {
    const n = parseInt(rawCategoryId, 10);
    categoryId = Number.isNaN(n) ? rawCategoryId : n;
  } else {
    categoryId = rawCategoryId ?? 0;
  }
  return {
    ...data,
    categoryId,
    imageUrl: data.image_url || data.imageUrl || '',
    imageUrlThumb: data.image_url_thumb || data.imageUrlThumb || undefined,
    isSignatureDish: data.is_signature_dish !== undefined ? data.is_signature_dish : data.isSignatureDish || false,
    ...normalizeMenuChannelFields(data),
  } as MenuItem;
}

export const menuItemsService = {
  async getAll(): Promise<MenuItem[]> {
    const items = await getAll<MenuItem>('menu_items');
    return items.map(normalizeMenuItemDoc);
  },

  async getById(id: string | number): Promise<MenuItem | null> {
    const item = await getById<MenuItem>('menu_items', String(id));
    return item ? normalizeMenuItemDoc(item) : null;
  },

  async create(item: Omit<MenuItem, 'id'>): Promise<MenuItem> {
    return create<MenuItem>('menu_items', item);
  },

  async update(id: string | number, updates: Partial<MenuItem>): Promise<MenuItem> {
    return update<MenuItem>('menu_items', String(id), updates);
  },

  async delete(id: string | number): Promise<void> {
    return remove('menu_items', String(id));
  },

  /**
   * Real-time subscription with auto-reconnect. Replaces the previous one-shot
   * fetch pattern in useMenuItems so a flaky network or a long-running tab
   * never leaves the POS with stale items.
   */
  subscribe(callback: (items: MenuItem[]) => void): Unsubscribe {
    const sub = createReconnectingSubscription<MenuItem>({
      name: 'menu_items',
      buildQuery: () => query(collection(getDB(), 'menu_items')),
      parseDoc: (snap) => normalizeMenuItemDoc({ id: snap.id, ...snap.data() } as Record<string, unknown>),
      callback,
    });
    return sub.unsubscribe;
  },
};

// ============ Customers ============

export const customersService = {
  async getAll(): Promise<Customer[]> {
    return getAll<Customer>('customers');
  },

  async getById(id: string | number): Promise<Customer | null> {
    return getById<Customer>('customers', String(id));
  },

  async create(customer: Omit<Customer, 'id'>): Promise<Customer> {
    return create<Customer>('customers', customer);
  },

  async update(id: string | number, updates: Partial<Customer>): Promise<Customer> {
    return update<Customer>('customers', String(id), updates);
  },

  async delete(id: string | number): Promise<void> {
    return remove('customers', String(id));
  },
};

// ============ Loyalty rewards (POS catalog → synced to customer app) ============

export const loyaltyRewardsService = {
  async getAll(): Promise<LoyaltyReward[]> {
    const rows = await getAll<LoyaltyReward & { id?: string | number }>('loyalty_rewards');
    return rows.map((r) => ({
      id: typeof r.id === 'number' ? r.id : Number(r.id) || 0,
      name: r.name,
      pointsRequired: Number(r.pointsRequired) || 0,
      discountValue: Number(r.discountValue) || 0,
      isActive: r.isActive !== false,
    }));
  },

  async save(reward: LoyaltyReward): Promise<LoyaltyReward> {
    const id = reward.id && reward.id > 0 ? reward.id : Date.now();
    const payload = {
      id,
      name: reward.name,
      pointsRequired: reward.pointsRequired,
      discountValue: reward.discountValue,
      isActive: reward.isActive !== false,
      updatedAt: Date.now(),
    };
    await createWithId('loyalty_rewards', String(id), payload as Omit<LoyaltyReward, 'id'> & { id: number; updatedAt: number });
    return { ...reward, id, isActive: payload.isActive };
  },

  async delete(rewardId: number): Promise<void> {
    return remove('loyalty_rewards', String(rewardId));
  },
};

// ============ App offers (POS-managed marketing offers for customer app) ============

export const appOffersService = {
  async getAll(): Promise<AppOffer[]> {
    return getAll<AppOffer>('app_offers');
  },

  async save(offer: AppOffer): Promise<AppOffer> {
    const id = offer.id?.trim() || `offer-${Date.now()}`;
    const payload: AppOffer = {
      ...offer,
      id,
      updatedAt: Date.now(),
      createdAt: offer.createdAt || Date.now(),
    };
    await createWithId('app_offers', id, payload as Omit<AppOffer, 'id'> & AppOffer);
    return payload;
  },

  async delete(offerId: string): Promise<void> {
    return remove('app_offers', offerId);
  },
};

export const appAnnouncementsService = {
  async getAll(): Promise<AppAnnouncement[]> {
    const rows = await getAll<AppAnnouncement>('app_announcements');
    return rows.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  },

  async save(row: AppAnnouncement): Promise<AppAnnouncement> {
    const id = row.id?.trim() || `ann-${Date.now()}`;
    const payload: AppAnnouncement = {
      ...row,
      id,
      updatedAt: Date.now(),
      createdAt: row.createdAt || Date.now(),
    };
    await createWithId('app_announcements', id, payload as Omit<AppAnnouncement, 'id'> & AppAnnouncement);
    return payload;
  },

  async delete(id: string): Promise<void> {
    return remove('app_announcements', id);
  },
};

// ============ Student meal plans — weekly booking (POS source of truth) ============

export const mealPlanTypesService = {
  async getAll(): Promise<MealPlanType[]> {
    const rows = await getAll<MealPlanType>('meal_plan_types');
    return rows
      .map((r) => ({
        id: String(r.id || ''),
        name: r.name || '',
        description: r.description || '',
        active: r.active !== false,
        bookingMode: (r.bookingMode === 'manual' ? 'manual' : 'auto') as MealPlanBookingMode,
        periods: (r.periods?.length ? r.periods : ['lunch']) as MealPeriod[],
        daysOfWeek: r.daysOfWeek?.length ? r.daysOfWeek : [1, 2, 3, 4, 5],
        priceCents: Number(r.priceCents) || 0,
        tcVersionRef: r.tcVersionRef || '2026-v1',
        sortOrder: Number(r.sortOrder) || 0,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  },

  async save(plan: MealPlanType): Promise<MealPlanType> {
    const id = plan.id?.trim() || `mpt-${Date.now()}`;
    const payload: MealPlanType = {
      ...plan,
      id,
      active: plan.active !== false,
      bookingMode: plan.bookingMode === 'manual' ? 'manual' : 'auto',
      periods: plan.periods?.length ? plan.periods : ['lunch'],
      daysOfWeek: plan.daysOfWeek?.length ? plan.daysOfWeek : [1, 2, 3, 4, 5],
      priceCents: Math.max(0, Math.floor(Number(plan.priceCents) || 0)),
      tcVersionRef: plan.tcVersionRef || '2026-v1',
      sortOrder: Number(plan.sortOrder) || 0,
      updatedAt: Date.now(),
      createdAt: plan.createdAt || Date.now(),
    };
    await createWithId('meal_plan_types', id, payload as Omit<MealPlanType, 'id'> & MealPlanType);
    return payload;
  },

  async delete(planTypeId: string): Promise<void> {
    return remove('meal_plan_types', planTypeId);
  },
};

export const mealPlanTcVersionsService = {
  async get(version: string): Promise<MealPlanTcVersion | null> {
    const id = String(version || '').trim();
    if (!id) return null;
    return getById<MealPlanTcVersion>('meal_plan_tc_versions', id);
  },

  async save(version: string, body: string): Promise<MealPlanTcVersion> {
    const id = String(version || '').trim() || '2026-v1';
    const payload: MealPlanTcVersion = {
      id,
      version: id,
      body: String(body || '').trim(),
      updatedAt: Date.now(),
    };
    await createWithId(
      'meal_plan_tc_versions',
      id,
      payload as Omit<MealPlanTcVersion, 'id'> & MealPlanTcVersion,
    );
    return payload;
  },
};

export const mealPlanSubscriptionsService = {
  async getAll(): Promise<MealPlanSubscription[]> {
    return getAll<MealPlanSubscription>('meal_plan_subscriptions');
  },

  async getById(id: string): Promise<MealPlanSubscription | null> {
    return getById<MealPlanSubscription>('meal_plan_subscriptions', id);
  },

  async getActive(): Promise<MealPlanSubscription[]> {
    const rows = await this.getAll();
    return rows.filter((s) => s.status === 'active');
  },

  async getByCustomerId(customerId: string | number): Promise<MealPlanSubscription[]> {
    const db = getFirestoreDB();
    const q = query(
      collection(db, 'meal_plan_subscriptions'),
      where('customerId', '==', String(customerId)),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as MealPlanSubscription));
  },

  async save(sub: MealPlanSubscription): Promise<MealPlanSubscription> {
    const id = sub.id?.trim() || `mps-${Date.now()}`;
    const payload: MealPlanSubscription = {
      ...sub,
      id,
      customerEmail: (sub.customerEmail || '').trim().toLowerCase(),
      updatedAt: Date.now(),
      createdAt: sub.createdAt || Date.now(),
    };
    await createWithId(
      'meal_plan_subscriptions',
      id,
      payload as Omit<MealPlanSubscription, 'id'> & MealPlanSubscription,
    );
    return payload;
  },
};

export const mealPlanWeeksService = {
  async getAll(): Promise<MealPlanWeek[]> {
    const rows = await getAll<MealPlanWeek>('meal_plan_weeks');
    return rows.sort((a, b) => (b.weekStartMs || 0) - (a.weekStartMs || 0));
  },

  async getById(weekId: string): Promise<MealPlanWeek | null> {
    return getById<MealPlanWeek>('meal_plan_weeks', weekId);
  },

  async save(week: MealPlanWeek): Promise<MealPlanWeek> {
    const id = week.id?.trim() || weekIdContaining();
    const bounds = weekBoundsMs(id);
    const payload: MealPlanWeek = {
      ...week,
      id,
      weekStartMs: week.weekStartMs || bounds.weekStartMs,
      weekEndMs: week.weekEndMs || bounds.weekEndMs,
      menu: week.menu && Object.keys(week.menu).length ? week.menu : emptyWeekMenu(id),
      updatedAt: Date.now(),
      createdAt: week.createdAt || Date.now(),
    };
    await createWithId('meal_plan_weeks', id, payload as Omit<MealPlanWeek, 'id'> & MealPlanWeek);
    return payload;
  },

  async ensureDraft(weekId: string): Promise<MealPlanWeek> {
    const existing = await this.getById(weekId);
    if (existing) return existing;
    const bounds = weekBoundsMs(weekId);
    return this.save({
      id: weekId,
      weekStartMs: bounds.weekStartMs,
      weekEndMs: bounds.weekEndMs,
      status: 'draft',
      menu: emptyWeekMenu(weekId),
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  },

  async copyWeek(fromWeekId: string, toWeekId: string): Promise<MealPlanWeek> {
    const source = await this.getById(fromWeekId);
    if (!source) throw new Error('Source week not found.');
    const bounds = weekBoundsMs(toWeekId);
    const fromDays = dayKeysForWeek(fromWeekId);
    const toDays = dayKeysForWeek(toWeekId);
    const menu: Record<string, MealPlanDayMenu> = emptyWeekMenu(toWeekId);
    fromDays.forEach((fromDay, i) => {
      const src = source.menu?.[fromDay];
      if (src) {
        menu[toDays[i]] = dayMenuAsText(src);
      }
    });
    return this.save({
      id: toWeekId,
      weekStartMs: bounds.weekStartMs,
      weekEndMs: bounds.weekEndMs,
      status: 'draft',
      copiedFromWeekId: fromWeekId,
      periods: source.periods?.length ? offeredPeriods(source.periods) : undefined,
      menu,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  },
};

export const weeklyBookingsService = {
  async getByWeekId(weekId: string): Promise<WeeklyBooking[]> {
    const db = getFirestoreDB();
    const q = query(collection(db, 'meal_plan_bookings'), where('weekId', '==', weekId));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as WeeklyBooking));
  },

  async getByCustomerId(customerId: string | number): Promise<WeeklyBooking[]> {
    const db = getFirestoreDB();
    const q = query(
      collection(db, 'meal_plan_bookings'),
      where('customerId', '==', String(customerId)),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as WeeklyBooking));
  },

  async getForCustomerDay(
    customerId: string | number,
    dayKey: string,
  ): Promise<WeeklyBooking[]> {
    const rows = await this.getByCustomerId(customerId);
    return rows.filter((b) => b.dayKey === dayKey);
  },

  async save(booking: WeeklyBooking): Promise<WeeklyBooking> {
    const id =
      booking.id?.trim() ||
      bookingDocId(booking.customerId, booking.dayKey, booking.period);
    const payload: WeeklyBooking = {
      ...booking,
      id,
      customerEmail: (booking.customerEmail || '').trim().toLowerCase(),
      updatedAt: Date.now(),
      createdAt: booking.createdAt || Date.now(),
    };
    await createWithId(
      'meal_plan_bookings',
      id,
      payload as Omit<WeeklyBooking, 'id'> & WeeklyBooking,
    );
    return payload;
  },
};

export const mealPlanExceptionsService = {
  async getAll(): Promise<MealPlanExceptionRequest[]> {
    const rows = await getAll<MealPlanExceptionRequest>('meal_plan_exception_requests');
    return rows.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  },

  async getPending(): Promise<MealPlanExceptionRequest[]> {
    return (await this.getAll()).filter((r) => r.status === 'pending');
  },

  async getByCustomerId(customerId: string | number): Promise<MealPlanExceptionRequest[]> {
    const db = getFirestoreDB();
    const q = query(
      collection(db, 'meal_plan_exception_requests'),
      where('customerId', '==', String(customerId)),
    );
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() } as MealPlanExceptionRequest))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  },

  async save(req: MealPlanExceptionRequest): Promise<MealPlanExceptionRequest> {
    const id = req.id?.trim() || `mpex-${Date.now()}`;
    const payload: MealPlanExceptionRequest = {
      ...req,
      id,
      customerEmail: (req.customerEmail || '').trim().toLowerCase(),
      updatedAt: Date.now(),
      createdAt: req.createdAt || Date.now(),
    };
    await createWithId(
      'meal_plan_exception_requests',
      id,
      payload as Omit<MealPlanExceptionRequest, 'id'> & MealPlanExceptionRequest,
    );
    return payload;
  },
};

export const mealPlanCostSettingsService = {
  async get(): Promise<MealPlanCostSettings> {
    const row = await getById<MealPlanCostSettings>('meal_plan_cost_settings', 'default');
    return (
      row || {
        id: 'default',
        foodCostPerMealCents: 500,
        laborCostPerMealCents: 300,
        overheadPercent: 15,
        updatedAt: Date.now(),
      }
    );
  },

  async save(settings: MealPlanCostSettings): Promise<MealPlanCostSettings> {
    const payload: MealPlanCostSettings = {
      ...settings,
      id: 'default',
      updatedAt: Date.now(),
    };
    await createWithId(
      'meal_plan_cost_settings',
      'default',
      payload as Omit<MealPlanCostSettings, 'id'> & MealPlanCostSettings,
    );
    return payload;
  },
};

export async function autoBookSubscriptionForWeek(
  sub: MealPlanSubscription,
  plan: MealPlanType,
  weekId: string,
): Promise<number> {
  let created = 0;
  const now = Date.now();
  const week = await mealPlanWeeksService.getById(weekId);
  const weekPeriods = offeredPeriods(week?.periods);
  for (const dayKey of dayKeysForWeek(weekId)) {
    const wd = isoWeekdayFromDayKey(dayKey);
    if (!plan.daysOfWeek.includes(wd)) continue;
    for (const period of plan.periods) {
      if (!weekPeriods.includes(period)) continue;
      const id = bookingDocId(sub.customerId, dayKey, period);
      const existing = await getById<WeeklyBooking>('meal_plan_bookings', id);
      if (existing) continue;
      await weeklyBookingsService.save({
        id,
        customerId: sub.customerId,
        customerEmail: sub.customerEmail,
        subscriptionId: sub.id,
        planTypeId: plan.id,
        weekId,
        dayKey,
        period,
        status: 'booked',
        createdAt: now,
        updatedAt: now,
      });
      created += 1;
    }
  }
  return created;
}

export function pickActiveSubscription(
  rows: MealPlanSubscription[],
  now = Date.now(),
): MealPlanSubscription | null {
  return (
    rows.find(
      (s) =>
        s.status === 'active' &&
        s.activeFrom <= now &&
        (!s.activeTo || s.activeTo >= now),
    ) || null
  );
}

export function getRedeemableBookingToday(
  bookings: WeeklyBooking[],
  period: MealPeriod,
  now = Date.now(),
): WeeklyBooking | null {
  const today = nzDateKey(now);
  return (
    bookings.find(
      (b) => b.dayKey === today && b.period === period && b.status === 'booked',
    ) || null
  );
}

/** @deprecated Legacy pack helpers — prefer weekly bookings. */
export const mealPlanProductsService = {
  async getAll(): Promise<MealPlanProduct[]> {
    const rows = await getAll<MealPlanProduct>('meal_plan_products');
    return rows as MealPlanProduct[];
  },
  async save(product: MealPlanProduct): Promise<MealPlanProduct> {
    const id = product.id?.trim() || `mp-${Date.now()}`;
    const payload = { ...product, id, updatedAt: Date.now(), createdAt: product.createdAt || Date.now() };
    await createWithId('meal_plan_products', id, payload as Omit<MealPlanProduct, 'id'> & MealPlanProduct);
    return payload;
  },
  async delete(productId: string): Promise<void> {
    return remove('meal_plan_products', productId);
  },
};

/** @deprecated */
export const mealPlanEntitlementsService = {
  async getAll(): Promise<MealPlanEntitlement[]> {
    return getAll<MealPlanEntitlement>('meal_plan_entitlements');
  },
  async getByCustomerId(customerId: string | number): Promise<MealPlanEntitlement[]> {
    const db = getFirestoreDB();
    const q = query(
      collection(db, 'meal_plan_entitlements'),
      where('customerId', '==', String(customerId)),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as MealPlanEntitlement));
  },
  async getByEmail(email: string): Promise<MealPlanEntitlement[]> {
    const normalized = email.trim().toLowerCase();
    if (!normalized) return [];
    const db = getFirestoreDB();
    const q = query(
      collection(db, 'meal_plan_entitlements'),
      where('customerEmail', '==', normalized),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as MealPlanEntitlement));
  },
  async save(entitlement: MealPlanEntitlement): Promise<MealPlanEntitlement> {
    const id = entitlement.id?.trim() || `mpe-${Date.now()}`;
    const payload = {
      ...entitlement,
      id,
      customerEmail: (entitlement.customerEmail || '').trim().toLowerCase(),
      updatedAt: Date.now(),
      createdAt: entitlement.createdAt || Date.now(),
    };
    await createWithId(
      'meal_plan_entitlements',
      id,
      payload as Omit<MealPlanEntitlement, 'id'> & MealPlanEntitlement,
    );
    return payload;
  },
  async delete(entitlementId: string): Promise<void> {
    return remove('meal_plan_entitlements', entitlementId);
  },
};

/** @deprecated */
export function pickActiveMealPlanEntitlement(
  rows: MealPlanEntitlement[],
  now = Date.now(),
): MealPlanEntitlement | null {
  const active = rows
    .filter((e) => {
      if (e.status === 'cancelled' || e.status === 'exhausted') return false;
      if ((e.mealsRemaining || 0) <= 0) return false;
      if (e.validUntil && e.validUntil < now) return false;
      if (e.status === 'expired') return false;
      return true;
    })
    .sort((a, b) => (a.validUntil || 0) - (b.validUntil || 0));
  return active[0] || null;
}

/** @deprecated */
export function isMealPlanRedeemedToday(
  entitlement: MealPlanEntitlement,
  now = Date.now(),
  timeZone = 'Pacific/Auckland',
): boolean {
  const dayKey = (ms: number) =>
    new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
      new Date(ms),
    );
  const today = dayKey(now);
  return (entitlement.redemptions || []).some((r) => dayKey(r.at) === today);
}

export { nextWeekId, weekIdContaining };

// ============ Coupon Campaigns ============

function generateCouponCode(): string {
  const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += charset[Math.floor(Math.random() * charset.length)];
  }
  return code;
}

function couponIsUnlimited(coupon: Pick<Coupon, 'usageLimit' | 'isPermanent'>): boolean {
  return coupon.isPermanent === true || coupon.usageLimit === null;
}

function normalizeCouponRecord(coupon: Coupon): Coupon {
  const unlimited = couponIsUnlimited(coupon);
  return {
    ...coupon,
    code: coupon.code?.trim().toUpperCase(),
    usageLimit: unlimited ? null : Math.max(1, Number(coupon.usageLimit ?? 1)),
    usedCount: Math.max(0, Number(coupon.usedCount ?? 0)),
    totalDiscountGiven: Math.max(0, Number(coupon.totalDiscountGiven ?? 0)),
    expiresAt: coupon.expiresAt ?? coupon.endDate ?? null,
  };
}

export { parseBuildingUnitList, formatBuildingUnitLabel, BUILDING_WING_LABEL } from './buildingUnitUtils';

export const couponCampaignsService = {
  async getAll(): Promise<CouponCampaign[]> {
    return getAll<CouponCampaign>('coupon_campaigns', orderBy('createdAt', 'desc'));
  },

  async getById(id: string): Promise<CouponCampaign | null> {
    return getById<CouponCampaign>('coupon_campaigns', id);
  },

  async create(campaign: Omit<CouponCampaign, 'id' | 'createdAt'>): Promise<CouponCampaign> {
    const kind = campaign.campaignKind ?? 'promotional';
    const defaultStatus =
      kind === 'building_unit' ? (campaign.status ?? 'published') : (campaign.status ?? 'draft');
    return create<CouponCampaign>('coupon_campaigns', {
      ...campaign,
      campaignKind: kind,
      status: defaultStatus,
      printCardAspectRatio:
        campaign.printCardAspectRatio ??
        (kind === 'building_unit' ? '86/54' : '85.6/54'),
      createdAt: Date.now(),
    } as Omit<CouponCampaign, 'id'>);
  },

  async update(id: string, updates: Partial<CouponCampaign>): Promise<CouponCampaign> {
    return update<CouponCampaign>('coupon_campaigns', id, updates);
  },

  async delete(id: string): Promise<void> {
    return remove('coupon_campaigns', id);
  },
};

// ============ Coupons ============
export const couponsService = {
  async getAll(): Promise<Coupon[]> {
    const coupons = await getAll<Coupon>('coupons', orderBy('startDate', 'desc'));
    return coupons.map(normalizeCouponRecord);
  },

  async getById(id: string): Promise<Coupon | null> {
    const coupon = await getById<Coupon>('coupons', id);
    return coupon ? normalizeCouponRecord(coupon) : null;
  },

  async getByCode(code: string): Promise<Coupon | null> {
    const db = getDB();
    const normalized = code.trim().toUpperCase();
    const q = query(collection(db, 'coupons'), where('code', '==', normalized), limit(1));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const d = snapshot.docs[0];
    return normalizeCouponRecord({ id: d.id, ...d.data() } as Coupon);
  },

  /** Loads coupons for one campaign only (indexed query on campaignId). */
  async getByCampaignId(campaignId: string): Promise<Coupon[]> {
    const list = await getAll<Coupon>('coupons', where('campaignId', '==', campaignId));
    return list.map(normalizeCouponRecord).sort((a, b) => (b.startDate ?? 0) - (a.startDate ?? 0));
  },

  async create(coupon: Omit<Coupon, 'id'>): Promise<Coupon> {
    const unlimited = couponIsUnlimited(coupon);
    const created = await create<Coupon>('coupons', {
      ...coupon,
      code: coupon.code.trim().toUpperCase(),
      usageLimit: unlimited ? null : Math.max(1, Number(coupon.usageLimit ?? 1)),
      usedCount: Math.max(0, Number(coupon.usedCount ?? 0)),
      totalDiscountGiven: Math.max(0, Number(coupon.totalDiscountGiven ?? 0)),
      expiresAt: coupon.expiresAt ?? coupon.endDate ?? null,
    });
    return normalizeCouponRecord(created);
  },

  async createBatchFromCampaign(
    campaignId: string,
    options?: {
      appliesTo?: 'all' | number[];
      startDate?: number;
      endDate?: number | null;
      expiresAt?: number | null;
      usageLimit?: number;
    }
  ): Promise<Coupon[]> {
    const campaign = await couponCampaignsService.getById(campaignId);
    if (!campaign) throw new Error('Campaign not found');
    const results: Coupon[] = [];
    const seenCodes = new Set<string>();
    const target = Math.max(0, campaign.totalCoupons || 0);
    for (let i = 0; i < target; i++) {
      let code = '';
      let attempts = 0;
      while (!code) {
        attempts += 1;
        if (attempts > 100) {
          throw new Error('Unable to generate a unique coupon code after multiple attempts.');
        }
        const candidate = generateCouponCode();
        if (seenCodes.has(candidate)) continue;
        const exists = await this.getByCode(candidate);
        if (exists) continue;
        code = candidate;
      }
      seenCodes.add(code);
      const created = await this.create({
        code,
        discountType: campaign.discountType,
        discountValue: campaign.discountValue,
        appliesTo: options?.appliesTo ?? 'all',
        startDate: options?.startDate ?? Date.now(),
        endDate: options?.endDate ?? campaign.expiryDate ?? null,
        expiresAt: options?.expiresAt ?? options?.endDate ?? campaign.expiryDate ?? null,
        status: 'ACTIVE',
        campaignId,
        usageLimit: Math.max(1, Number(options?.usageLimit ?? 1)),
        usedCount: 0,
      });
      results.push(created);
    }
    return results;
  },

  async createBatchFromUnitList(
    campaignId: string,
    unitLabels: string[],
    options?: {
      buildingWing?: Coupon['buildingWing'];
      startDate?: number;
    }
  ): Promise<Coupon[]> {
    const campaign = await couponCampaignsService.getById(campaignId);
    if (!campaign) throw new Error('Campaign not found');
    if (campaign.campaignKind !== 'building_unit') {
      throw new Error('Campaign is not a building unit campaign');
    }
    const buildingWing = options?.buildingWing ?? campaign.buildingWing;
    if (!buildingWing) throw new Error('Building wing (Hotel Wing or Tower Wing) is required');

    const normalizedLabels = unitLabels.map((l) => l.trim()).filter(Boolean);
    const labelKeys = new Set<string>();
    for (const label of normalizedLabels) {
      const key = label.toLowerCase();
      if (labelKeys.has(key)) {
        throw new Error(`Duplicate unit in list: "${label}"`);
      }
      labelKeys.add(key);
    }

    const existingCampaignCoupons = await this.getByCampaignId(campaignId);
    const existingLabels = new Set(
      existingCampaignCoupons.map((c) => (c.unitLabel ?? '').trim().toLowerCase()).filter(Boolean)
    );

    const results: Coupon[] = [];
    const seenCodes = new Set<string>();
    for (const unitLabel of normalizedLabels) {
      if (existingLabels.has(unitLabel.toLowerCase())) {
        throw new Error(`Unit "${unitLabel}" already has a card in this campaign`);
      }
      let code = '';
      let attempts = 0;
      while (!code) {
        attempts += 1;
        if (attempts > 100) {
          throw new Error('Unable to generate a unique coupon code after multiple attempts.');
        }
        const candidate = generateCouponCode();
        if (seenCodes.has(candidate)) continue;
        const exists = await this.getByCode(candidate);
        if (exists) continue;
        code = candidate;
      }
      seenCodes.add(code);
      const created = await this.create({
        code,
        discountType: campaign.discountType,
        discountValue: campaign.discountValue,
        appliesTo: 'all',
        startDate: options?.startDate ?? Date.now(),
        endDate: null,
        expiresAt: null,
        status: 'ACTIVE',
        campaignId,
        usageLimit: null,
        usedCount: 0,
        totalDiscountGiven: 0,
        isPermanent: true,
        unitLabel,
        buildingWing,
      });
      results.push(created);
    }

    await couponCampaignsService.update(campaignId, {
      totalCoupons: existingCampaignCoupons.length + results.length,
    });

    return results;
  },

  async update(id: string, updates: Partial<Coupon>): Promise<Coupon> {
    const usageLimitPatch =
      updates.usageLimit !== undefined
        ? updates.usageLimit === null
          ? null
          : Math.max(1, Number(updates.usageLimit))
        : undefined;
    const updated = await update<Coupon>('coupons', id, {
      ...updates,
      ...(updates.code ? { code: updates.code.trim().toUpperCase() } : {}),
      ...(usageLimitPatch !== undefined ? { usageLimit: usageLimitPatch } : {}),
      ...(updates.usedCount !== undefined ? { usedCount: Math.max(0, Number(updates.usedCount)) } : {}),
      ...(updates.totalDiscountGiven !== undefined
        ? { totalDiscountGiven: Math.max(0, Number(updates.totalDiscountGiven)) }
        : {}),
      ...(updates.expiresAt !== undefined ? { expiresAt: updates.expiresAt ?? null } : {}),
    });
    return normalizeCouponRecord(updated);
  },

  async isValidForRedemption(coupon: Coupon, now = Date.now()): Promise<{ ok: boolean; reason?: string }> {
    if (coupon.status !== 'ACTIVE') return { ok: false, reason: `Coupon is ${coupon.status.toLowerCase()}` };
    if (coupon.startDate && now < coupon.startDate) return { ok: false, reason: 'Coupon is not active yet' };
    if (!coupon.isPermanent && coupon.expiresAt && now > coupon.expiresAt) {
      return { ok: false, reason: 'Coupon has expired' };
    }
    if (!couponIsUnlimited(coupon)) {
      const usageLimit = Math.max(1, Number(coupon.usageLimit ?? 1));
      if ((coupon.usedCount ?? 0) >= usageLimit) {
        return { ok: false, reason: 'Coupon usage limit reached' };
      }
    }
    return { ok: true };
  },

  async markUsed(params: {
    couponId: string;
    orderId: string;
    staffId?: number;
    discountAmount?: number;
  }): Promise<Coupon> {
    const db = getDB();
    const couponRef = doc(db, 'coupons', params.couponId);
    const updatedCoupon = await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(couponRef);
      if (!snapshot.exists()) {
        throw new Error('Coupon not found');
      }
      const coupon = normalizeCouponRecord({ id: snapshot.id, ...snapshot.data() } as Coupon);
      const usedCount = Math.max(0, Number(coupon.usedCount ?? 0));
      const unlimited = couponIsUnlimited(coupon);
      const usageLimit = unlimited ? null : Math.max(1, Number(coupon.usageLimit ?? 1));
      if (coupon.status !== 'ACTIVE') {
        throw new Error(`Coupon is ${coupon.status.toLowerCase()}`);
      }
      if (!unlimited && usageLimit !== null && usedCount >= usageLimit) {
        throw new Error('Coupon usage limit reached');
      }
      const nextUsedCount = usedCount + 1;
      const discountDelta = Math.max(0, Number(params.discountAmount ?? 0));
      const nextData: Partial<Coupon> = {
        usedCount: nextUsedCount,
        usedByOrderId: params.orderId,
        usedAt: Date.now(),
        appliedByStaffId: params.staffId,
      };
      if (unlimited) {
        nextData.status = 'ACTIVE';
        nextData.totalDiscountGiven = Math.max(0, Number(coupon.totalDiscountGiven ?? 0)) + discountDelta;
      } else {
        nextData.status = nextUsedCount >= (usageLimit ?? 1) ? 'USED' : 'ACTIVE';
      }
      transaction.update(couponRef, nextData);
      return { ...coupon, ...nextData };
    });
    return normalizeCouponRecord(updatedCoupon as Coupon);
  },

  async markExpired(id: string): Promise<Coupon> {
    return update<Coupon>('coupons', id, { status: 'EXPIRED' });
  },

  async delete(id: string): Promise<void> {
    return remove('coupons', id);
  },
};

// ============ Coupon Usage Logs ============
export const couponUsageLogsService = {
  async create(entry: Omit<CouponUsageLog, 'id'>): Promise<CouponUsageLog> {
    return create<CouponUsageLog>('coupon_usage_logs', entry as CouponUsageLog);
  },

  async getRecent(limitCount = 50): Promise<CouponUsageLog[]> {
    return getAll<CouponUsageLog>(
      'coupon_usage_logs',
      orderBy('attemptedAt', 'desc'),
      limit(Math.max(1, limitCount))
    );
  },
};

export const couponRedemptionsService = {
  async create(entry: Omit<CouponRedemption, 'id'>): Promise<CouponRedemption> {
    return create<CouponRedemption>('coupon_redemptions', entry as CouponRedemption);
  },

  async getRecent(limitCount = 100): Promise<CouponRedemption[]> {
    return getAll<CouponRedemption>(
      'coupon_redemptions',
      orderBy('redeemedAt', 'desc'),
      limit(Math.max(1, limitCount))
    );
  },
};

export const couponAnalyticsService = {
  async getCampaignStats(campaignId: string): Promise<{
    totalIssued: number;
    totalUsed: number;
    remaining: number;
    expired: number;
  }> {
    const allCoupons = (await getAll<Coupon>('coupons', where('campaignId', '==', campaignId))).map(normalizeCouponRecord);
    const totalIssued = allCoupons.length;
    const totalUsed = allCoupons.filter(
      (c) => c.isPermanent ? (c.usedCount ?? 0) > 0 : c.status === 'USED'
    ).length;
    const expired = allCoupons.filter((c) => c.status === 'EXPIRED').length;
    return {
      totalIssued,
      totalUsed,
      remaining: Math.max(0, totalIssued - totalUsed - expired),
      expired,
    };
  },
};

// ============ Staff ============

/** Produce a stable positive numeric id from a string (e.g. Firestore doc id) to avoid duplicate ids when doc has no numeric id. */
function staffDocIdToNumber(firestoreId: string): number {
  let h = 0;
  for (let i = 0; i < firestoreId.length; i++) {
    h = ((h << 5) - h) + firestoreId.charCodeAt(i);
    h = h & 0x7fffffff;
  }
  return h || 1;
}

export const staffService = {
  async getAll(): Promise<Staff[]> {
    const db = getDB();
    const snapshot = await getDocs(collection(db, 'staff'));
    return snapshot.docs.map((doc) => {
      const data = doc.data();
      const firestoreId = doc.id;
      const rawId = data.id;
      const numericId = (typeof rawId === 'number' && !Number.isNaN(rawId))
        ? rawId
        : staffDocIdToNumber(firestoreId);
      return {
        ...data,
        id: numericId,
        firestoreId,
      } as Staff & { firestoreId: string };
    });
  },

  async getById(id: string): Promise<Staff | null> {
    const result = await getById<Staff>('staff', id);
    if (!result) return null;
    // Convert Firestore string ID to number to match StaffMember interface
    return {
      ...result,
      id: typeof result.id === 'string' ? parseInt(result.id, 10) || 0 : (result.id as number),
    };
  },

  async getByEmail(email: string): Promise<Staff | null> {
    const db = getDB();
    const q = query(collection(db, 'staff'), where('email', '==', email.toLowerCase()), limit(1));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    const staff = { id: doc.id, ...doc.data() } as Staff & { firestoreId: string };
    // Convert Firestore string ID to number to match StaffMember interface
    return {
      ...staff,
      id: typeof staff.id === 'string' ? parseInt(staff.id, 10) || 0 : (staff.id as number),
      firestoreId: doc.id,
    };
  },

  async getByPin(pin: string): Promise<Staff | null> {
    const db = getDB();
    const q = query(collection(db, 'staff'), where('pin', '==', pin), limit(1));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    const staff = { id: doc.id, ...doc.data() } as Staff;
    // Convert Firestore string ID to number to match StaffMember interface
    return {
      ...staff,
      id: typeof staff.id === 'string' ? parseInt(staff.id, 10) || 0 : (staff.id as number),
    };
  },

  async getByAuthUserId(authUserId: string): Promise<Staff[]> {
    const db = getDB();
    const q = query(collection(db, 'staff'), where('authUserId', '==', authUserId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => {
      const staff = { id: doc.id, ...doc.data() } as Staff;
      return {
        ...staff,
        id: typeof staff.id === 'string' ? parseInt(staff.id, 10) || 0 : (staff.id as number),
        firestoreId: doc.id,
      } as Staff & { firestoreId: string };
    });
  },

  async create(staff: Omit<Staff, 'id'>): Promise<Staff> {
    const existing = await this.getAll();
    const maxId = existing.length > 0 ? Math.max(...existing.map((s) => s.id), 0) : 0;
    const newId = maxId + 1;
    const db = getDB();
    const collectionRef = collection(db, 'staff');
    const payload = removeUndefined({ ...staff, id: newId } as Record<string, any>);
    const docRef = await addDoc(collectionRef, payload);
    const docSnap = await getDoc(docRef);
    const data = docSnap.data();
    return {
      ...data,
      id: newId,
      firestoreId: docRef.id,
    } as Staff & { firestoreId: string };
  },

  async update(id: string | number, updates: Partial<Staff>): Promise<Staff> {
    const db = getDB();
    const idString = String(id);
    let firestoreIdToUse: string;
    const docRef = doc(db, 'staff', idString);
    const directDoc = await getDoc(docRef);
    if (directDoc.exists()) {
      firestoreIdToUse = idString;
    } else {
      // Resolve numeric id to firestoreId (same as delete)
      const allStaff = await this.getAll();
      const targetId = typeof id === 'string' ? parseInt(id, 10) : Number(id);
      if (isNaN(targetId)) throw new Error(`Staff member with ID ${id} not found.`);
      const staffMember = allStaff.find((s) => (typeof s.id === 'string' ? parseInt(s.id, 10) : Number(s.id)) === targetId);
      if (!staffMember) throw new Error(`Staff member with ID ${id} not found.`);
      const fid = (staffMember as Staff & { firestoreId?: string }).firestoreId;
      if (!fid) throw new Error(`Staff member "${staffMember.name}" has no firestoreId. Cannot update.`);
      firestoreIdToUse = fid;
    }
    const result = await update<Staff>('staff', firestoreIdToUse, updates);
    const data = result as Staff & { firestoreId?: string };
    const numericId = typeof data.id === 'number' && !Number.isNaN(data.id) ? data.id : (parseInt(String(data.id), 10) || 0);
    return {
      ...data,
      id: numericId,
      firestoreId: firestoreIdToUse,
    } as Staff & { firestoreId: string };
  },

  async delete(id: string | number): Promise<void> {
    // The id parameter can be either:
    // 1. A Firestore document ID (string, e.g., "abc123xyz")
    // 2. A numeric ID from the UI (number, e.g., 5)
    // Strategy: First try to delete directly by Firestore ID, if that fails, lookup by numeric ID
    
    try {
      const idString = String(id);
      console.log(`🔍 Attempting to delete staff member with ID: ${idString} (type: ${typeof id})`);
      
      // First, try to get the document directly by Firestore ID
      // This handles the case where firestoreId is passed directly
      try {
        const db = getDB();
    const docRef = doc(db, 'staff', idString);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const staffData = docSnap.data();
          console.log(`✅ Found staff member directly by Firestore ID: ${staffData.name} (${idString})`);
          console.log(`🗑️  Deleting Firestore document: ${idString}`);
          await remove('staff', idString);
          console.log(`✅ Staff member "${staffData.name}" deleted successfully`);
          return;
        }
      } catch (directGetError) {
        // Document doesn't exist with that ID, continue to lookup by numeric ID
        console.log(`⚠️  Document ${idString} not found directly, trying numeric ID lookup...`);
      }
      
      // If direct lookup failed, try to find by numeric ID
      // Get all staff to find the correct firestoreId
      const allStaff = await this.getAll();
      console.log(`📋 getAll() returned ${allStaff.length} staff members`);
      
      // Convert id to number for comparison
      const targetId = typeof id === 'string' ? parseInt(id, 10) : Number(id);
      
      if (isNaN(targetId)) {
        throw new Error(`Invalid ID format: ${id}. Expected a Firestore document ID or numeric ID.`);
      }
      
      // Find the staff member with matching numeric ID
      const staffMember = allStaff.find(s => {
        const staffNumericId = typeof s.id === 'string' ? parseInt(s.id, 10) : Number(s.id);
        return staffNumericId === targetId;
      });
      
      if (!staffMember) {
        const allIds = allStaff.map(s => `${s.name} (id: ${s.id}, firestoreId: ${(s as any).firestoreId})`).join(', ');
        throw new Error(`Staff member with ID ${id} not found. Available: ${allIds}`);
      }
      
      const firestoreId = (staffMember as any).firestoreId;
      if (!firestoreId) {
        throw new Error(`Staff member "${staffMember.name}" (numeric ID: ${id}) has no firestoreId. Cannot delete.`);
      }
      
      console.log(`✅ Found staff member by numeric ID: ${staffMember.name} (numeric ID: ${targetId}, firestoreId: ${firestoreId})`);
      console.log(`🗑️  Deleting Firestore document: ${firestoreId}`);
      
      // Delete using the actual Firestore document ID
      await remove('staff', firestoreId);
      console.log(`✅ Staff member "${staffMember.name}" deleted successfully (firestoreId: ${firestoreId})`);
    } catch (error) {
      console.error('❌ Error deleting staff member:', error);
      throw error;
    }
  },

  // Real-time subscription
  subscribe(callback: (staff: Staff[]) => void): Unsubscribe {
    const db = getDB();
    const q = query(collection(db, 'staff'), orderBy('name', 'asc'));
    return onSnapshot(q, (snapshot) => {
      const staff = snapshot.docs.map((doc) => {
        const data = doc.data();
        const firestoreId = doc.id;
        const rawId = data.id;
        // Use stored numeric id when valid; otherwise derive unique id from firestoreId so we never have duplicate ids
        const numericId = (typeof rawId === 'number' && !Number.isNaN(rawId))
          ? rawId
          : staffDocIdToNumber(firestoreId);
        return {
          ...data,
          id: numericId,
          firestoreId,
        } as Staff & { firestoreId: string };
      });
      callback(staff);
    }, (error) => {
      console.error('Error in staff subscription:', error);
    });
  },
};

// ============ Inventory ============

export const inventoryService = {
  async getAll(): Promise<InventoryItem[]> {
    return getAll<InventoryItem>('inventory');
  },

  async getById(id: string | number): Promise<InventoryItem | null> {
    return getById<InventoryItem>('inventory', String(id));
  },

  async create(item: Omit<InventoryItem, 'id'>): Promise<InventoryItem> {
    return create<InventoryItem>('inventory', item);
  },

  async update(id: string | number, updates: Partial<InventoryItem>): Promise<InventoryItem> {
    return update<InventoryItem>('inventory', String(id), updates);
  },

  async delete(id: string | number): Promise<void> {
    return remove('inventory', String(id));
  },
};

// ============ Asset register (reusable kit; not inventory) ============

export const assetsService = {
  async getAll(): Promise<RestaurantAsset[]> {
    return getAll<RestaurantAsset>('assets');
  },

  async getById(id: string): Promise<RestaurantAsset | null> {
    return getById<RestaurantAsset>('assets', id);
  },

  async create(item: Omit<RestaurantAsset, 'id'> & { id?: string }): Promise<RestaurantAsset> {
    const id = item.id ?? `AST-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { id: _drop, ...data } = item as RestaurantAsset;
    return createWithId<RestaurantAsset>('assets', id, data);
  },

  async update(id: string, updates: Partial<RestaurantAsset>): Promise<RestaurantAsset> {
    return update<RestaurantAsset>('assets', id, updates);
  },

  async delete(id: string): Promise<void> {
    return remove('assets', id);
  },
};

// ============ Tables ============

/** Produce a stable unique numeric id from a Firestore doc id when the table document has no numeric id (avoids all tables getting id 0 and orders matching every table). */
function tableDocIdToNumber(docId: string): number {
  let h = 0;
  for (let i = 0; i < docId.length; i++) {
    h = ((h << 5) - h) + docId.charCodeAt(i);
    h = h & 0x7fffffff;
  }
  return h || 1;
}

export const tablesService = {
  async getAll(): Promise<Table[]> {
    const db = getDB();
    const snapshot = await getDocs(collection(db, 'tables'));
    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      const rawId = (data as any).id;
      let numericId: number;
      if (typeof rawId === 'number' && Number.isFinite(rawId)) {
        numericId = rawId;
      } else if (typeof rawId === 'string' && rawId.trim() !== '' && !Number.isNaN(parseInt(rawId, 10))) {
        numericId = parseInt(rawId, 10);
      } else {
        // Fall back to stable hash of Firestore doc id
        numericId = tableDocIdToNumber(docSnap.id);
      }
      return { ...(data as any), id: numericId, firestoreId: docSnap.id } as Table;
    });
  },

  async getById(id: string | number): Promise<Table | null> {
    return getById<Table>('tables', String(id));
  },

  async create(table: Omit<Table, 'id'>): Promise<Table> {
    const db = getDB();
    const collectionRef = collection(db, 'tables');
    const cleanedData = removeUndefined(table as Record<string, any>);
    const docRef = await addDoc(collectionRef, cleanedData);
    const docSnap = await getDoc(docRef);
    const data = docSnap.data() || {};
    const rawId = (data as { id?: number | string }).id;
    let numericId: number;
    if (typeof rawId === 'number' && Number.isFinite(rawId)) {
      numericId = rawId;
    } else if (typeof rawId === 'string' && rawId.trim() !== '' && !Number.isNaN(parseInt(rawId, 10))) {
      numericId = parseInt(rawId, 10);
    } else {
      numericId = tableDocIdToNumber(docRef.id);
    }
    return { ...(data as object), id: numericId, firestoreId: docRef.id } as Table;
  },

  async update(id: string | number, updates: Partial<Table>, firestoreId?: string): Promise<Table> {
    const idStr = String(id);
    const db = getDB();

    if (firestoreId) {
      const byFirestoreId = doc(db, 'tables', firestoreId);
      const byFirestoreSnap = await getDoc(byFirestoreId);
      if (byFirestoreSnap.exists()) {
        const existing = byFirestoreSnap.data() as Table;
        const cleanedUpdates = removeUndefined(updates as Record<string, any>);
        const unchanged = Object.entries(cleanedUpdates).every(
          ([key, value]) => (existing as Record<string, unknown>)[key] === value,
        );
        if (unchanged) {
          const numId = typeof id === 'number' ? id : parseInt(idStr, 10);
          return { ...existing, id: Number.isNaN(numId) ? existing.id : numId, firestoreId };
        }
        await updateDoc(byFirestoreId, cleanedUpdates);
        const updated = await getDoc(byFirestoreId);
        const data = updated.data() as Table;
        const numId = typeof id === 'number' ? id : parseInt(idStr, 10);
        return { ...data, id: Number.isNaN(numId) ? data.id : numId, firestoreId };
      }
    }

    const docRef = doc(db, 'tables', idStr);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return update<Table>('tables', idStr, updates);
    }
    // Document not found by string id (e.g. id is numeric 5 but doc was created with addDoc and has auto id)
    const snapshot = await getDocs(collection(db, 'tables'));
    const numId = typeof id === 'number' ? id : parseInt(idStr, 10);
    if (Number.isNaN(numId)) throw new Error(`Table with ID ${id} not found.`);
    const found = snapshot.docs.find((d) => {
      const data = d.data();
      const dataId = data.id;
      if (dataId === numId || dataId === idStr || String(dataId) === idStr) return true;
      // Match by hashed Firestore doc id when UI uses tableDocIdToNumber(doc.id) as table.id
      return tableDocIdToNumber(d.id) === numId;
    });
    if (!found) throw new Error(`Table with ID ${id} not found.`);
    const cleanedUpdates = removeUndefined(updates as Record<string, any>);
    await updateDoc(found.ref, cleanedUpdates);
    const updated = await getDoc(found.ref);
    return { id: numId, firestoreId: found.id, ...updated.data() } as Table;
  },

  async delete(id: string | number, firestoreId?: string): Promise<void> {
    const idStr = String(id);
    const db = getDB();

    if (firestoreId) {
      const byFirestoreId = doc(db, 'tables', firestoreId);
      const byFirestoreSnap = await getDoc(byFirestoreId);
      if (byFirestoreSnap.exists()) {
        await deleteDoc(byFirestoreId);
        return;
      }
    }

    const docRef = doc(db, 'tables', idStr);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      await deleteDoc(docRef);
      return;
    }
    // Document not found by string id (table was created with addDoc and has auto-generated doc id)
    const snapshot = await getDocs(collection(db, 'tables'));
    const numId = typeof id === 'number' ? id : parseInt(idStr, 10);
    if (Number.isNaN(numId)) throw new Error(`Table with ID ${id} not found.`);
    const found = snapshot.docs.find((d) => {
      const data = d.data();
      const dataId = data.id;
      if (dataId === numId || dataId === idStr || String(dataId) === idStr) return true;
      return tableDocIdToNumber(d.id) === numId;
    });
    if (!found) throw new Error(`Table with ID ${id} not found.`);
    await deleteDoc(found.ref);
  },

  subscribe(callback: (tables: Table[]) => void): Unsubscribe {
    const db = getDB();
    const collectionRef = collection(db, 'tables');
    return onSnapshot(collectionRef, (snapshot) => {
      const tables = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const rawId = data.id;
        const id = (typeof rawId === 'number' && !Number.isNaN(rawId))
          ? rawId
          : (typeof rawId === 'string' ? parseInt(rawId, 10) : NaN);
        const numericId = Number.isNaN(id) ? tableDocIdToNumber(docSnap.id) : id;
        return { ...data, id: numericId, firestoreId: docSnap.id } as Table;
      });
      callback(tables);
    }, (error) => {
      console.error('Tables subscription error:', error);
    });
  },
};

// ============ Events ============

export const eventsService = {
  async getAll(): Promise<Event[]> {
    return getAll<Event>('events', orderBy('eventDate', 'asc'));
  },

  async getById(id: string | number): Promise<Event | null> {
    return getById<Event>('events', String(id));
  },

  async create(event: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>): Promise<Event> {
    const now = Date.now();
    const data = { ...event, createdAt: now, updatedAt: now };
    return create<Event>('events', data as Omit<Event, 'id'>);
  },

  async update(id: string | number, updates: Partial<Event>): Promise<Event> {
    const cleaned = { ...updates, updatedAt: Date.now() };
    return update<Event>('events', String(id), cleaned);
  },

  async delete(id: string | number): Promise<void> {
    return remove('events', String(id));
  },

  async getByDate(date: string): Promise<Event[]> {
    const constraints: QueryConstraint[] = [
      where('eventDate', '==', date),
      orderBy('eventDate', 'asc'),
    ];
    return getAll<Event>('events', ...constraints);
  },
};

// ============ Reservations ============

export const reservationsService = {
  async getAll(): Promise<Reservation[]> {
    return getAll<Reservation>('reservations', orderBy('reservationDate', 'asc'));
  },

  async getById(id: string | number): Promise<Reservation | null> {
    return getById<Reservation>('reservations', String(id));
  },

  async create(reservation: Omit<Reservation, 'id'>): Promise<Reservation> {
    return create<Reservation>('reservations', reservation);
  },

  async update(id: string | number, updates: Partial<Reservation>): Promise<Reservation> {
    return update<Reservation>('reservations', String(id), updates);
  },

  async delete(id: string | number): Promise<void> {
    return remove('reservations', String(id));
  },

  async getByDate(date: string): Promise<Reservation[]> {
    const constraints: QueryConstraint[] = [
      where('reservationDate', '==', date),
      orderBy('reservationTime', 'asc'),
    ];
    return getAll<Reservation>('reservations', ...constraints);
  },

  async getByTableId(tableId: number): Promise<Reservation[]> {
    const constraints: QueryConstraint[] = [
      where('tableId', '==', tableId),
      orderBy('reservationDate', 'asc'),
    ];
    return getAll<Reservation>('reservations', ...constraints);
  },
};

// ============ Register Sessions ============

/** Live listener window for register session history (reports + register close). */
const REGISTER_SESSIONS_LIVE_LISTENER_MAX_AGE_MS = 120 * 24 * 60 * 60 * 1000;

export function mergeRegisterSessionsById(...lists: RegisterSession[][]): RegisterSession[] {
  const map = new Map<string, RegisterSession>();
  for (const list of lists) {
    for (const session of list) {
      if (!session?.id) continue;
      const existing = map.get(session.id);
      map.set(
        session.id,
        existing ? mergeRegisterSessionRecords(existing, session) : normalizeRegisterSession(session),
      );
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => (toMillis(b.startTime) || 0) - (toMillis(a.startTime) || 0),
  );
}

/** Ignore stale offline cache snapshots that report zero rows before the server responds. */
function shouldIgnoreEmptyRegisterSnapshot(snapshot: QuerySnapshot): boolean {
  return snapshot.empty && snapshot.metadata.fromCache && !snapshot.metadata.hasPendingWrites;
}

function mapRegisterSessionDocs(snapshot: QuerySnapshot): RegisterSession[] {
  return snapshot.docs
    .map((docSnap) => normalizeRegisterSession({ ...docSnap.data(), id: docSnap.id } as RegisterSession))
    .sort((a, b) => (toMillis(b.startTime) || 0) - (toMillis(a.startTime) || 0));
}

export const registerSessionsService = {
  /**
   * Active sessions only (same filter as {@link subscribeActive}); avoids scanning full history.
   */
  async getActiveSessions(options?: { preferServer?: boolean }): Promise<RegisterSession[]> {
    const q = query(collection(getDB(), 'register_sessions'), where('isActive', '==', true));
    const snapshot = options?.preferServer
      ? await getDocsFromServer(q)
      : await getDocs(q);
    return mapRegisterSessionDocs(snapshot);
  },

  /** Bounded history + active sessions — same shape as {@link subscribe}. */
  async getLiveSnapshot(): Promise<RegisterSession[]> {
    const now = Date.now();
    const cutoff = now - REGISTER_SESSIONS_LIVE_LISTENER_MAX_AGE_MS;
    const [range, active] = await Promise.all([
      this.getByDateRange(cutoff, now),
      this.getActiveSessions({ preferServer: true }),
    ]);
    return mergeRegisterSessionsById([range, active]);
  },

  async getAll(): Promise<RegisterSession[]> {
    return getAll<RegisterSession>('register_sessions', orderBy('startTime', 'desc'));
  },

  async getByDateRange(start: number, end: number): Promise<RegisterSession[]> {
    const constraints: QueryConstraint[] = [
      orderBy('startTime', 'asc'),
      where('startTime', '>=', start),
      where('startTime', '<=', end),
    ];
    return getAll<RegisterSession>('register_sessions', ...constraints);
  },

  async getById(id: string | number): Promise<RegisterSession | null> {
    return getById<RegisterSession>('register_sessions', String(id));
  },

  /**
   * Create a new register session.
   *
   * **Layer 3 of the duplicate-session defense.** Before writing, we query
   * `register_sessions where isActive == true`. If any row exists we abort
   * with code `DUPLICATE_ACTIVE_REGISTER` so the caller can surface the
   * shared-session UI instead of creating a parallel session.
   *
   * This is the deepest guard - sits below the in-memory check in App.tsx
   * (Layer 1: UI hydration gate) and the pre-flight in handleOpenRegister
   * (Layer 2). Catches scripts, future code paths, and any race the upper
   * layers miss. Caller can opt out by passing `isActive: false`
   * (e.g. when restoring an already-closed session).
   */
  async create(session: Omit<RegisterSession, 'id'>): Promise<RegisterSession> {
    if (session.isActive !== false) {
      const snapshot = await getDocs(query(
        collection(getDB(), 'register_sessions'),
        where('isActive', '==', true),
      ));
      if (!snapshot.empty) {
        const existingDoc = snapshot.docs[0];
        const existing = existingDoc.data() as RegisterSession;
        const err = new Error(
          `Active register session already exists (id=${existingDoc.id}, cashier=${existing.cashierName ?? 'unknown'})`
        );
        (err as any).code = 'DUPLICATE_ACTIVE_REGISTER';
        (err as any).existingSession = { id: existingDoc.id, ...existing } as RegisterSession;
        throw err;
      }
    }
    return create<RegisterSession>('register_sessions', session);
  },

  async update(id: string | number, updates: Partial<RegisterSession>): Promise<RegisterSession> {
    return update<RegisterSession>('register_sessions', String(id), updates);
  },

  async delete(id: string | number): Promise<void> {
    return remove('register_sessions', String(id));
  },

  /**
   * Real-time listener for ALL active register sessions (no terminal filter).
   * This is the shared-register model: any device that signs in sees the same active session.
   *
   * - Filters server-side: `isActive == true` only (small result set, no full collection scan).
   * - Sorts client-side by `startTime` descending so the caller can take [0] as the
   *   "current" session if multiple are unexpectedly active (e.g. legacy per-terminal rows).
   * - Avoids server-side orderBy so we don't need a composite index.
   */
  subscribeActive(
    callback: (sessions: RegisterSession[]) => void
  ): Unsubscribe {
    const db = getDB();
    const q = query(
      collection(db, 'register_sessions'),
      where('isActive', '==', true)
    );
    return onSnapshot(
      q,
      (snapshot) => {
        if (shouldIgnoreEmptyRegisterSnapshot(snapshot)) return;
        callback(mapRegisterSessionDocs(snapshot));
      },
      (error) => {
        console.error('❌ Active register sessions subscription error:', error);
      }
    );
  },

  // Real-time subscription — bounded recent history plus active sessions (shared register).
  // History query is best-effort (needs composite index); active query always runs and
  // must not be blocked if the range listener fails.
  subscribe(
    callback: (sessions: RegisterSession[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const db = getDB();
    let rangeSessions: RegisterSession[] = [];
    let activeOnly: RegisterSession[] = [];

    const emit = () => callback(mergeRegisterSessionsById([rangeSessions, activeOnly]));

    const cutoff = Date.now() - REGISTER_SESSIONS_LIVE_LISTENER_MAX_AGE_MS;
    const rangeQuery = query(
      collection(db, 'register_sessions'),
      where('startTime', '>=', cutoff),
      orderBy('startTime', 'desc'),
    );
    const activeQuery = query(
      collection(db, 'register_sessions'),
      where('isActive', '==', true),
    );

    const unsubRange = onSnapshot(
      rangeQuery,
      (snapshot) => {
        if (shouldIgnoreEmptyRegisterSnapshot(snapshot)) return;
        rangeSessions = snapshot.docs.map((docSnap) =>
          normalizeRegisterSession({ ...docSnap.data(), id: docSnap.id } as RegisterSession),
        );
        emit();
      },
      (error) => {
        console.error('❌ Register sessions range subscription error (history may be stale):', error);
        rangeSessions = [];
        emit();
        onError?.(error);
      },
    );

    const unsubActive = onSnapshot(
      activeQuery,
      (snapshot) => {
        if (shouldIgnoreEmptyRegisterSnapshot(snapshot)) return;
        activeOnly = mapRegisterSessionDocs(snapshot);
        emit();
      },
      (error) => {
        console.error('❌ Active register sessions subscription error:', error);
        activeOnly = [];
        emit();
        onError?.(error);
      },
    );

    return () => {
      unsubRange();
      unsubActive();
    };
  },

  // Real-time subscription with date range filter
  subscribeByDateRange(
    start: number,
    end: number,
    callback: (sessions: RegisterSession[]) => void
  ): Unsubscribe {
    const db = getDB();
    const q = query(
      collection(db, 'register_sessions'),
      where('startTime', '>=', start),
      where('startTime', '<=', end),
      orderBy('startTime', 'asc')
    );
    return onSnapshot(q, (snapshot) => {
      const sessions = snapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id } as RegisterSession));
      callback(sessions);
    });
  },
};

// ============ Recipes ============
// Stored by menuItemId: each document id = String(menuItemId), data = { menuItemId, ingredients }

export const recipesService = {
  async getAll(): Promise<Recipe[]> {
    const db = getDB();
    const snapshot = await getDocs(collection(db, 'recipes'));
    return snapshot.docs.map((d) => {
      const data = d.data();
      return {
        menuItemId: data.menuItemId ?? Number(d.id),
        ingredients: data.ingredients ?? [],
      } as Recipe;
    });
  },

  async save(recipe: Recipe): Promise<Recipe> {
    const db = getDB();
    const id = String(recipe.menuItemId);
    const docRef = doc(db, 'recipes', id);
    const payload = removeUndefined({ menuItemId: recipe.menuItemId, ingredients: recipe.ingredients } as Record<string, unknown>);
    await setDoc(docRef, payload, { merge: true });
    return recipe;
  },

  async deleteByMenuItemId(menuItemId: number): Promise<void> {
    await remove('recipes', String(menuItemId));
  },
};

// ============ Quick Add Combos ============

export const quickAddCombosService = {
  async getAll(): Promise<QuickAddCombo[]> {
    return getAll<QuickAddCombo>('quick_add_combos');
  },

  async getById(id: string | number): Promise<QuickAddCombo | null> {
    return getById<QuickAddCombo>('quick_add_combos', String(id));
  },

  async create(combo: Omit<QuickAddCombo, 'id'>): Promise<QuickAddCombo> {
    return create<QuickAddCombo>('quick_add_combos', combo);
  },

  async update(id: string | number, updates: Partial<QuickAddCombo>): Promise<QuickAddCombo> {
    return update<QuickAddCombo>('quick_add_combos', String(id), updates);
  },

  async delete(id: string | number): Promise<void> {
    return remove('quick_add_combos', String(id));
  },
};

// ============ Floor Fixtures ============

export const floorFixturesService = {
  async getAll(): Promise<FloorFixture[]> {
    return getAll<FloorFixture>('floor_fixtures');
  },

  async getById(id: string): Promise<FloorFixture | null> {
    return getById<FloorFixture>('floor_fixtures', id);
  },

  async create(fixture: Omit<FloorFixture, 'id'>): Promise<FloorFixture> {
    return create<FloorFixture>('floor_fixtures', fixture);
  },

  async upsert(fixture: FloorFixture): Promise<FloorFixture> {
    const { id, ...data } = fixture;
    if (!id) {
      return create<FloorFixture>('floor_fixtures', data as Omit<FloorFixture, 'id'>);
    }
    const db = getDB();
    const docRef = doc(db, 'floor_fixtures', id);
    await setDoc(docRef, data, { merge: true });
    const docSnap = await getDoc(docRef);
    return { id: docSnap.id, ...docSnap.data() } as FloorFixture;
  },

  async update(id: string, updates: Partial<FloorFixture>): Promise<FloorFixture> {
    return update<FloorFixture>('floor_fixtures', id, updates);
  },

  async delete(id: string): Promise<void> {
    return remove('floor_fixtures', id);
  },

  async seedDefaults(fixtures: FloorFixture[]): Promise<FloorFixture[]> {
    const results: FloorFixture[] = [];
    const db = getDB();
    for (const fixture of fixtures) {
      const { id, ...data } = fixture;
      const fixtureId = id || doc(collection(db, 'floor_fixtures')).id;
      const docRef = doc(db, 'floor_fixtures', fixtureId);
      await setDoc(docRef, data, { merge: true });
      results.push({ ...fixture, id: fixtureId });
    }
    return results;
  },
};

// ============ Security Services ============
// These services are for security features (trusted devices, 2FA, etc.)

export const trustedDevicesService = {
  async getAll(): Promise<any[]> {
    return getAll<any>('trusted_devices');
  },
  async getById(id: string): Promise<any | null> {
    return getById<any>('trusted_devices', id);
  },
  async create(device: any): Promise<any> {
    return create<any>('trusted_devices', device);
  },
  async update(id: string, updates: any): Promise<any> {
    return update<any>('trusted_devices', id, updates);
  },
  async delete(id: string): Promise<void> {
    return remove('trusted_devices', id);
  },
};

export const loginSessionsService = {
  async getAll(): Promise<any[]> {
    return getAll<any>('login_sessions');
  },
  async getById(id: string): Promise<any | null> {
    return getById<any>('login_sessions', id);
  },
  async create(session: any): Promise<any> {
    return create<any>('login_sessions', session);
  },
  async update(id: string, updates: any): Promise<any> {
    return update<any>('login_sessions', id, updates);
  },
  async delete(id: string): Promise<void> {
    return remove('login_sessions', id);
  },
};

export const loginAttemptsService = {
  async getAll(): Promise<any[]> {
    return getAll<any>('login_attempts');
  },
  async getById(id: string): Promise<any | null> {
    return getById<any>('login_attempts', id);
  },
  async create(attempt: any): Promise<any> {
    return create<any>('login_attempts', attempt);
  },
  async update(id: string, updates: any): Promise<any> {
    return update<any>('login_attempts', id, updates);
  },
  async delete(id: string): Promise<void> {
    return remove('login_attempts', id);
  },
};

export const securitySettingsService = {
  async getAll(): Promise<any[]> {
    return getAll<any>('security_settings');
  },
  async getById(id: string): Promise<any | null> {
    return getById<any>('security_settings', id);
  },
  async create(setting: any): Promise<any> {
    return create<any>('security_settings', setting);
  },
  async update(id: string, updates: any): Promise<any> {
    return update<any>('security_settings', id, updates);
  },
  async delete(id: string): Promise<void> {
    return remove('security_settings', id);
  },
};

export const integrationSettingsService = {
  // Integration settings are stored as a single document with ID 'main'
  async get(): Promise<any | null> {
    try {
      console.log('📦 Loading integration settings from Firestore...');
      const db = getDB();
    const docRef = doc(db, 'integration_settings', 'main');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('✅ Integration settings loaded from Firestore:', data);
        return data;
      } else {
        console.log('⚠️ No integration settings found in Firestore, using defaults');
        return null;
      }
    } catch (error) {
      console.error('❌ Error loading integration settings:', error);
      throw error;
    }
  },

  async save(settings: any): Promise<void> {
    try {
      console.log('💾 Saving integration settings to Firestore...', settings);
      
      // Remove undefined values recursively (Firestore doesn't allow undefined)
      const cleanSettings = this.removeUndefinedValues(settings);
      
      const db = getDB();
    const docRef = doc(db, 'integration_settings', 'main');
      await setDoc(docRef, cleanSettings, { merge: true });
      console.log('✅ Integration settings saved successfully to Firestore');
    } catch (error) {
      console.error('❌ Error saving integration settings:', error);
      throw error;
    }
  },

  /**
   * Recursively remove undefined values from an object
   * Firestore doesn't allow undefined values
   */
  removeUndefinedValues(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }
    
    if (Array.isArray(obj)) {
      return obj
        .map(item => this.removeUndefinedValues(item))
        .filter(item => item !== undefined);
    }
    
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const value = obj[key];
          if (value !== undefined) {
            cleaned[key] = this.removeUndefinedValues(value);
          }
        }
      }
      return cleaned;
    }
    
    return obj;
  },
};

const PUBLIC_ONLINE_ORDERING_DOC = 'online_ordering';

/** Public config for online ordering (readable without auth). Used when viewMode is 'online'. */
export const publicOnlineOrderingService = {
  async get(): Promise<PublicOnlineOrderingConfig | null> {
    try {
      const db = getDB();
      const docRef = doc(db, 'public_config', PUBLIC_ONLINE_ORDERING_DOC);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data() as PublicOnlineOrderingConfig;
      }
      return null;
    } catch (error) {
      console.debug('Public online ordering config not loaded:', (error as Error)?.message);
      return null;
    }
  },

  /** Sync public doc from integration settings (call after saving integration settings). */
  async syncFromSettings(input: {
    businessInfo?:
      | {
          onlineOrderingEnabled?: boolean;
          mealPlansEnabled?: boolean;
          businessName?: string;
          address?: string;
          phone?: string;
          email?: string;
          onlineOrderStartTime?: string;
          onlineOrderEndTime?: string;
        }
      | undefined;
    businessHours?: BusinessHours | null;
  }): Promise<void> {
    try {
      const businessInfo = input.businessInfo;
      const db = getDB();
      const docRef = doc(db, 'public_config', PUBLIC_ONLINE_ORDERING_DOC);
      const schedule = publicOrderingSchedule(input.businessHours);
      const payload: PublicOnlineOrderingConfig = {
        onlineOrderingEnabled: businessInfo?.onlineOrderingEnabled === true,
        mealPlansEnabled: businessInfo?.mealPlansEnabled === true,
        businessName: businessInfo?.businessName,
        address: businessInfo?.address?.trim() || undefined,
        phone: businessInfo?.phone?.trim() || undefined,
        email: businessInfo?.email?.trim() || undefined,
      };
      const cleaned = integrationSettingsService.removeUndefinedValues(payload) as Record<string, unknown>;
      if (schedule) {
        cleaned.timezone = schedule.timezone;
        cleaned.orderingDays = schedule.days;
        cleaned.onlineOrderStartTime = deleteField();
        cleaned.onlineOrderEndTime = deleteField();
      } else {
        if (businessInfo?.onlineOrderStartTime) cleaned.onlineOrderStartTime = businessInfo.onlineOrderStartTime;
        if (businessInfo?.onlineOrderEndTime) cleaned.onlineOrderEndTime = businessInfo.onlineOrderEndTime;
      }
      await setDoc(docRef, cleaned, { merge: true });
    } catch (error) {
      console.error('Failed to sync public online ordering config:', error);
      throw error;
    }
  },
};

export const rosterService = {
  async getRosterByWeek(weekStart: number): Promise<WeeklyRoster | null> {
    try {
      const db = getDB();
    const docRef = doc(db, 'rosters', `week-${weekStart}`);
      const snap = await getDoc(docRef);
      if (!snap.exists()) {
        return null;
      }
      return { id: docRef.id, ...snap.data() } as WeeklyRoster;
    } catch (error) {
      console.error('❌ Error loading roster:', error);
      throw error;
    }
  },

  async saveRoster(roster: WeeklyRoster): Promise<WeeklyRoster> {
    try {
      const db = getDB();
    const docRef = doc(db, 'rosters', `week-${roster.weekStart}`);
      const payload = {
        ...roster,
        id: docRef.id,
        createdAt: roster.createdAt ?? Date.now(),
        updatedAt: Date.now(),
      };
      const cleaned = removeUndefined(payload as Record<string, any>);
      await setDoc(docRef, cleaned, { merge: false });
      return payload;
    } catch (error) {
      console.error('❌ Error saving roster:', error);
      throw error;
    }
  },

  async updateShifts(weekStart: number, shifts: Shift[]): Promise<void> {
    try {
      const db = getDB();
    const docRef = doc(db, 'rosters', `week-${weekStart}`);
      await setDoc(
        docRef,
        {
          shifts,
          updatedAt: Date.now(),
        },
        { merge: true }
      );
    } catch (error) {
      console.error('❌ Error updating roster shifts:', error);
      throw error;
    }
  },

  /** Submit roster for approval (status → submitted). Caller should pass current roster and approver label. */
  async submitRoster(weekStart: number, submittedBy: string): Promise<void> {
    const db = getDB();
    const docRef = doc(db, 'rosters', `week-${weekStart}`);
    await setDoc(
      docRef,
      {
        status: 'submitted',
        submittedAt: Date.now(),
        submittedBy,
        updatedAt: Date.now(),
      },
      { merge: true }
    );
  },

  /** Approve roster (status → approved). Caller should pass approver label. */
  async approveRoster(weekStart: number, approvedBy: string): Promise<void> {
    const db = getDB();
    const docRef = doc(db, 'rosters', `week-${weekStart}`);
    await setDoc(
      docRef,
      {
        status: 'approved',
        approvedAt: Date.now(),
        approvedBy,
        updatedAt: Date.now(),
      },
      { merge: true }
    );
  },

  /** Reopen roster to draft (e.g. after a mistake). Keeps submitted/approved fields for audit. */
  async reopenRoster(weekStart: number): Promise<void> {
    const db = getDB();
    const docRef = doc(db, 'rosters', `week-${weekStart}`);
    await setDoc(
      docRef,
      { status: 'draft', updatedAt: Date.now() },
      { merge: true }
    );
  },
};

// ============ Timesheet entries (manual entries, e.g. overtime) ============

export const timesheetService = {
  async getByDateRange(startDate: number, endDate: number, staffId?: number): Promise<TimesheetEntry[]> {
    try {
      const db = getDB();
      const colRef = collection(db, 'timesheet_entries');
      const constraints: QueryConstraint[] = [
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date'),
      ];
      if (staffId != null) {
        constraints.unshift(where('staffId', '==', staffId));
      }
      const q = query(colRef, ...constraints);
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as TimesheetEntry));
    } catch (error) {
      console.error('❌ Error loading timesheet entries:', error);
      throw error;
    }
  },

  async add(entry: Omit<TimesheetEntry, 'id' | 'createdAt' | 'updatedAt'>): Promise<TimesheetEntry> {
    try {
      const db = getDB();
      const colRef = collection(db, 'timesheet_entries');
      const now = Date.now();
      const payload = removeUndefined({
        ...entry,
        createdAt: now,
        updatedAt: now,
      } as Record<string, any>);
      const docRef = await addDoc(colRef, payload);
      return { id: docRef.id, ...payload } as TimesheetEntry;
    } catch (error) {
      console.error('❌ Error adding timesheet entry:', error);
      throw error;
    }
  },

  async update(id: string, updates: Partial<Omit<TimesheetEntry, 'id'>>): Promise<void> {
    try {
      const db = getDB();
      const docRef = doc(db, 'timesheet_entries', id);
      await updateDoc(docRef, { ...updates, updatedAt: Date.now() });
    } catch (error) {
      console.error('❌ Error updating timesheet entry:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const db = getDB();
      const docRef = doc(db, 'timesheet_entries', id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('❌ Error deleting timesheet entry:', error);
      throw error;
    }
  },
};

// ============ Payslips (persisted payroll records) ============

export const payslipService = {
  /** Create or update a payslip for a staff member + pay period (id is derived from those fields). */
  async upsert(
    record: Omit<PayslipRecord, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<PayslipRecord> {
    try {
      const db = getDB();
      const colRef = collection(db, 'payslips');
      const id = `${record.staffId}-${record.payPeriodStart}-${record.payPeriodEnd}`;
      const docRef = doc(colRef, id);
      const now = Date.now();
      const basePayload: PayslipRecord = {
        ...record,
        createdAt: now,
        updatedAt: now,
      };
      const payload = removeUndefined(basePayload as Record<string, any>);
      await setDoc(docRef, payload, { merge: true });
      const snap = await getDoc(docRef);
      const data = snap.data() || {};
      return { id: docRef.id, ...(data as any) } as PayslipRecord;
    } catch (error) {
      console.error('❌ Error upserting payslip record:', error);
      throw error;
    }
  },

  /** Load all payslips for a given pay period (by start/end date strings). */
  async getByPeriod(payPeriodStart: string, payPeriodEnd: string): Promise<PayslipRecord[]> {
    try {
      const db = getDB();
      const colRef = collection(db, 'payslips');
      const q = query(
        colRef,
        where('payPeriodStart', '==', payPeriodStart),
        where('payPeriodEnd', '==', payPeriodEnd)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as PayslipRecord));
    } catch (error) {
      console.error('❌ Error loading payslips by period:', error);
      throw error;
    }
  },
};

// ============ User Roles ============

export const userRolesService = {
  async getAll(): Promise<any[]> {
    // Query Firestore directly to get full control over the data structure
    const db = getDB();
    const collectionRef = collection(db, 'user_roles');
    const snapshot = await getDocs(collectionRef);
    return snapshot.docs.map((doc) => {
      const data = doc.data();
      const firestoreId = doc.id;
      // Check if the document data has an 'id' field (role's logical ID)
      // If not, use the Firestore document ID as the role ID
      // This handles both cases:
      // 1. Roles stored with their ID as document ID (new way) - doc.id === data.id
      // 2. Roles stored with auto-generated IDs but have id field in data (old way)
      // 3. Roles stored with auto-generated IDs and no id field in data - use doc.id
      const roleId = (data as any).id || firestoreId;
      return {
        ...data,
        id: roleId,
        firestoreId: firestoreId, // Preserve Firestore document ID
      };
    });
  },

  async initializeDefaultRoles(): Promise<void> {
    // Import initial roles
    const { initialUserRoles } = await import('../data/userRoles');
    
    console.log('🚀 Initializing default user roles in Firestore...');
    
    for (const role of initialUserRoles) {
      try {
        const db = getDB();
        const docRef = doc(db, 'user_roles', role.id);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
          // Create only missing roles — never overwrite customised permissions on existing docs
          await setDoc(docRef, {
            name: role.name,
            permissions: role.permissions,
            id: role.id,
          });
          console.log(`✅ Created role: ${role.name} (${role.id})`);
        } else {
          console.log(`⏭️ Role already exists: ${role.name} (${role.id})`);
        }
      } catch (error) {
        console.error(`❌ Error initializing role ${role.name}:`, error);
        throw error;
      }
    }
    
    console.log('✅ Default user roles initialized');
  },

  /** Create any seeded roles that are missing from Firestore (safe for existing sites). */
  async ensureMissingDefaultRoles(): Promise<string[]> {
    const { initialUserRoles } = await import('../data/userRoles');
    const created: string[] = [];
    const db = getDB();
    for (const role of initialUserRoles) {
      try {
        const docRef = doc(db, 'user_roles', role.id);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
          await setDoc(docRef, {
            name: role.name,
            permissions: role.permissions,
            id: role.id,
          });
          created.push(role.id);
          console.log(`✅ Added missing role: ${role.name} (${role.id})`);
        }
      } catch (error) {
        console.error(`❌ Error ensuring role ${role.name}:`, error);
      }
    }
    return created;
  },
  async getById(id: string): Promise<any | null> {
    // First try to get by Firestore document ID
    const result = await getById<any>('user_roles', id);
    if (result) {
      return result;
    }
    // If not found, query by role id field
    const db = getDB();
    const q = query(collection(db, 'user_roles'), where('id', '==', id), limit(1));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    return { id: doc.data().id || doc.id, ...doc.data() };
  },
  async create(role: Omit<any, 'id'>, id?: string): Promise<any> {
    // Always use createWithId to store role with its ID as the document ID
    const roleId = id || (role as any).id;
    if (!roleId) {
      throw new Error('Role ID is required when creating a role');
    }
    // Store the role with its ID as the document ID
    const roleData = { ...role, id: roleId };
    return createWithId<any>('user_roles', roleId, roleData);
  },
  async update(id: string, updates: Partial<any>): Promise<any> {
    // First try to update by Firestore document ID (in case document ID matches role ID)
    try {
      const result = await update<any>('user_roles', id, updates);
      return result;
    } catch (error: any) {
      // If document not found by ID, search all documents to find the matching role
      if (error.code === 'not-found' || error.message?.includes('No document to update')) {
        console.log(`🔍 Role with document ID "${id}" not found, searching all roles...`);
        
        // Use this service's getAll() method which properly handles the id field
        const allRoles = await this.getAll();
        let matchingRole: any = null;
        let firestoreId: string | null = null;
        
        // Search through all roles
        for (const roleData of allRoles) {
          // Check if role id matches (this is the logical role ID like 'cashier')
          if (roleData.id === id) {
            matchingRole = roleData;
            firestoreId = roleData.firestoreId;
            break;
          }
          // Also check if Firestore document ID matches
          if (roleData.firestoreId === id) {
            matchingRole = roleData;
            firestoreId = roleData.firestoreId;
            break;
          }
        }
        
        // If still not found, try querying Firestore directly (in case roles don't have id field in data)
        if (!matchingRole) {
          console.log(`🔍 Still not found, querying Firestore directly...`);
          const db = getDB();
    const collectionRef = collection(db, 'user_roles');
          const snapshot = await getDocs(collectionRef);
          
          for (const doc of snapshot.docs) {
            const data = doc.data();
            // Check if document ID matches
            if (doc.id === id) {
              firestoreId = doc.id;
              matchingRole = { id: data.id || doc.id, ...data, firestoreId: doc.id };
              break;
            }
            // Check if role id field in data matches
            if ((data as any).id === id) {
              firestoreId = doc.id;
              matchingRole = { id: (data as any).id || doc.id, ...data, firestoreId: doc.id };
              break;
            }
          }
        }
        
        if (!matchingRole || !firestoreId) {
          throw new Error(`Role with ID "${id}" not found in Firestore. Available roles: ${allRoles.map(r => `${r.name} (id: ${r.id}, firestoreId: ${r.firestoreId})`).join(', ')}`);
        }
        
        console.log(`✅ Found role "${matchingRole.name}" with Firestore ID: ${firestoreId}`);
        // Update using the Firestore document ID
        return await update<any>('user_roles', firestoreId, updates);
      }
      throw error;
    }
  },
  async delete(id: string): Promise<void> {
    // First try to delete by Firestore document ID
    try {
      await remove('user_roles', id);
    } catch (error: any) {
      // If document not found by ID, try to find by role id field
      if (error.code === 'not-found' || error.message?.includes('No document to update')) {
        const db = getDB();
    const q = query(collection(db, 'user_roles'), where('id', '==', id), limit(1));
        const snapshot = await getDocs(q);
        if (snapshot.empty) {
          throw new Error(`Role with ID "${id}" not found`);
        }
        const doc = snapshot.docs[0];
        const firestoreId = doc.id;
        await remove('user_roles', firestoreId);
      } else {
        throw error;
      }
    }
  },
  subscribe(callback: (roles: any[]) => void): () => void {
    const db = getDB();
    const collectionRef = collection(db, 'user_roles');
    const unsubscribe = onSnapshot(collectionRef, (snapshot) => {
      const roles = snapshot.docs.map((doc) => {
        const data = doc.data();
        // Preserve the role's id field from data, or use Firestore doc ID as fallback
        const roleId = data.id || doc.id;
        return {
          ...data,
          id: roleId,
          firestoreId: doc.id, // Preserve Firestore document ID
        };
      });
      callback(roles);
    });
    return unsubscribe;
  },
};

// ============ Overseas Shipments ============
export const shipmentService = {
  async getAll(): Promise<OverseasShipment[]> {
    return getAll<OverseasShipment>('overseas_shipments', orderBy('dateCreated', 'desc'));
  },

  async getById(id: string): Promise<OverseasShipment | null> {
    return getById<OverseasShipment>('overseas_shipments', id);
  },

  async create(shipment: Omit<OverseasShipment, 'id'>): Promise<OverseasShipment> {
    const db = getDB();
    const collectionRef = collection(db, 'overseas_shipments');
    const cleaned = removeUndefined(shipment);
    const docRef = await addDoc(collectionRef, cleaned);
    return { id: docRef.id, ...shipment } as OverseasShipment;
  },

  async update(id: string, updates: Partial<OverseasShipment>): Promise<void> {
    const db = getDB();
    const docRef = doc(db, 'overseas_shipments', id);
    const cleaned = removeUndefined(updates);
    await updateDoc(docRef, cleaned);
  },

  async delete(id: string): Promise<void> {
    const db = getDB();
    const docRef = doc(db, 'overseas_shipments', id);
    await deleteDoc(docRef);
  },

  subscribe(callback: (shipments: OverseasShipment[]) => void): Unsubscribe {
    const db = getDB();
    const q = query(collection(db, 'overseas_shipments'), orderBy('dateCreated', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const shipments = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as OverseasShipment[];
      callback(shipments);
    });
    return unsubscribe;
  },
};

// ============ Suppliers ============
export const suppliersService = {
  async getAll(): Promise<Supplier[]> {
    const rows = await getAll<Supplier & { id: string | number }>('suppliers');
    return rows.map((row) => ({
      ...row,
      id: typeof row.id === 'number' ? row.id : Number(row.id) || Date.now(),
    }));
  },

  async create(supplier: Omit<Supplier, 'id'> & { id?: number }): Promise<Supplier> {
    const id = supplier.id ?? Date.now();
    const { id: _drop, ...rest } = supplier;
    const payload = removeUndefined({ ...rest, id } as Record<string, unknown>);
    await createWithId('suppliers', String(id), payload);
    return { ...rest, id } as Supplier;
  },

  async update(id: number, updates: Partial<Supplier>): Promise<void> {
    const db = getDB();
    const docRef = doc(db, 'suppliers', String(id));
    await updateDoc(docRef, removeUndefined(updates as Record<string, unknown>));
  },

  async delete(id: number): Promise<void> {
    return remove('suppliers', String(id));
  },

  subscribe(callback: (suppliers: Supplier[]) => void): Unsubscribe {
    const db = getDB();
    const q = query(collection(db, 'suppliers'));
    return onSnapshot(
      q,
      (snapshot) => {
        const suppliers = snapshot.docs.map((d) => {
          const data = d.data() as Omit<Supplier, 'id'>;
          const numericId = typeof data.id === 'number' ? data.id : Number(d.id) || 0;
          return { ...data, id: numericId };
        });
        callback(suppliers);
      },
      (err) => {
        console.warn('[suppliers] listener:', (err as { code?: string })?.code ?? err);
      },
    );
  },
};

// ============ Supplier product lists (one current file per supplier) ============
export const supplierCatalogsService = {
  async getAll(): Promise<SupplierCatalog[]> {
    const rows = await getAll<SupplierCatalog & { id?: string }>('supplier_catalogs');
    return rows.map((row) => ({
      supplierId: Number(row.supplierId) || Number((row as { id?: string }).id) || 0,
      fileName: row.fileName ?? '',
      uploadedAt: row.uploadedAt ?? 0,
      items: Array.isArray(row.items) ? row.items : [],
    }));
  },

  /** Overwrites the supplier's current list. Previous products are not kept. */
  async replace(catalog: SupplierCatalog): Promise<void> {
    const db = getDB();
    const payload = removeUndefined({
      supplierId: catalog.supplierId,
      fileName: catalog.fileName,
      uploadedAt: catalog.uploadedAt,
      items: catalog.items,
    } as Record<string, unknown>);
    await setDoc(doc(db, 'supplier_catalogs', String(catalog.supplierId)), payload);
  },

  subscribe(callback: (catalogs: SupplierCatalog[]) => void): Unsubscribe {
    const db = getDB();
    const q = query(collection(db, 'supplier_catalogs'));
    return onSnapshot(
      q,
      (snapshot) => {
        const catalogs = snapshot.docs.map((d) => {
          const data = d.data() as Omit<SupplierCatalog, 'supplierId'> & { supplierId?: number };
          return {
            supplierId: Number(data.supplierId) || Number(d.id) || 0,
            fileName: data.fileName ?? '',
            uploadedAt: data.uploadedAt ?? 0,
            items: Array.isArray(data.items) ? data.items : [],
          };
        });
        callback(catalogs);
      },
      (err) => {
        console.warn('[supplier_catalogs] listener:', (err as { code?: string })?.code ?? err);
      },
    );
  },
};

// ============ Purchase Orders ============
export const purchaseOrdersService = {
  async getAll(): Promise<PurchaseOrder[]> {
    return getAll<PurchaseOrder>('purchase_orders', orderBy('dateCreated', 'desc'));
  },

  async getById(id: string): Promise<PurchaseOrder | null> {
    return getById<PurchaseOrder>('purchase_orders', id);
  },

  async create(po: Omit<PurchaseOrder, 'id'> & { id?: string }): Promise<PurchaseOrder> {
    const { nextPurchaseOrderId, bumpPurchaseOrderId } = await import('./purchaseOrderId');
    const { id: _drop, ...data } = po as PurchaseOrder;
    let id = po.id?.trim() || nextPurchaseOrderId([]);
    const db = getDB();
    for (let attempt = 0; attempt < 8; attempt++) {
      const docRef = doc(db, 'purchase_orders', id);
      try {
        await runTransaction(db, async (tx) => {
          const existing = await tx.get(docRef);
          if (existing.exists()) throw new Error('PO_ID_COLLISION');
          tx.set(docRef, removeUndefined(data as Record<string, unknown>));
        });
        return { ...(data as Omit<PurchaseOrder, 'id'>), id };
      } catch (err) {
        if (err instanceof Error && err.message === 'PO_ID_COLLISION' && attempt < 7) {
          id = bumpPurchaseOrderId(id);
          continue;
        }
        throw err;
      }
    }
    throw new Error('Could not allocate a purchase order number.');
  },

  async update(id: string, updates: Partial<PurchaseOrder>): Promise<void> {
    const db = getDB();
    const docRef = doc(db, 'purchase_orders', id);
    await updateDoc(docRef, removeUndefined(updates as Record<string, unknown>));
  },

  async delete(id: string): Promise<void> {
    return remove('purchase_orders', id);
  },

  subscribe(callback: (orders: PurchaseOrder[]) => void): Unsubscribe {
    const db = getDB();
    const q = query(collection(db, 'purchase_orders'), orderBy('dateCreated', 'desc'));
    return onSnapshot(
      q,
      (snapshot) => {
        const orders = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as PurchaseOrder[];
        callback(orders);
      },
      (err) => {
        console.warn('[purchase_orders] listener:', (err as { code?: string })?.code ?? err);
      },
    );
  },
};

// ============ Billing clients (B2B invoice recipients) ============
export const billingClientsService = {
  async getAll(): Promise<BillingClient[]> {
    const rows = await getAll<BillingClient & { id: string | number }>('billing_clients');
    return rows.map((row) => ({
      ...row,
      id: typeof row.id === 'number' ? row.id : Number(row.id) || Date.now(),
    }));
  },

  async create(client: Omit<BillingClient, 'id'> & { id?: number }): Promise<BillingClient> {
    const id = client.id ?? Date.now();
    const { id: _drop, ...rest } = client;
    const payload = removeUndefined({ ...rest, id } as Record<string, unknown>);
    await createWithId('billing_clients', String(id), payload);
    return { ...rest, id } as BillingClient;
  },

  async update(id: number, updates: Partial<BillingClient>): Promise<void> {
    const db = getDB();
    await updateDoc(doc(db, 'billing_clients', String(id)), removeUndefined(updates as Record<string, unknown>));
  },

  async delete(id: number): Promise<void> {
    return remove('billing_clients', String(id));
  },

  subscribe(
    callback: (clients: BillingClient[]) => void,
    onError?: (error: unknown) => void,
  ): Unsubscribe {
    const db = getDB();
    return onSnapshot(
      query(collection(db, 'billing_clients')),
      (snapshot) => {
        const clients = snapshot.docs.map((d) => {
          const data = d.data() as BillingClient;
          return { ...data, id: Number(d.id) || data.id };
        });
        callback(clients);
      },
      (err) => {
        console.warn('[billing_clients] listener:', (err as { code?: string })?.code ?? err);
        onError?.(err);
      },
    );
  },
};

// ============ Supplier invoices (incoming) ============
export const supplierInvoicesService = {
  async getAll(): Promise<SupplierInvoice[]> {
    return getAll<SupplierInvoice>('supplier_invoices', orderBy('dateCreated', 'desc'));
  },

  async create(invoice: Omit<SupplierInvoice, 'id'> & { id?: string }): Promise<SupplierInvoice> {
    const id = invoice.id ?? `SINV-${Date.now()}`;
    const { id: _drop, ...data } = invoice;
    return createWithId<SupplierInvoice>('supplier_invoices', id, data);
  },

  async update(id: string, updates: Partial<SupplierInvoice>): Promise<void> {
    const db = getDB();
    await updateDoc(doc(db, 'supplier_invoices', id), removeUndefined(updates as Record<string, unknown>));
  },

  async delete(id: string): Promise<void> {
    return remove('supplier_invoices', id);
  },

  subscribe(
    callback: (invoices: SupplierInvoice[]) => void,
    onError?: (error: unknown) => void,
  ): Unsubscribe {
    const db = getDB();
    const q = query(collection(db, 'supplier_invoices'), orderBy('dateCreated', 'desc'));
    return onSnapshot(
      q,
      (snapshot) => {
        callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as SupplierInvoice[]);
      },
      (err) => {
        console.warn('[supplier_invoices] listener:', (err as { code?: string })?.code ?? err);
        onError?.(err);
      },
    );
  },
};

// ============ Client invoices (outgoing) ============
export const clientInvoicesService = {
  async getAll(): Promise<ClientInvoice[]> {
    return getAll<ClientInvoice>('client_invoices', orderBy('dateCreated', 'desc'));
  },

  async create(invoice: Omit<ClientInvoice, 'id'> & { id?: string }): Promise<ClientInvoice> {
    const id = invoice.id ?? `CINV-${Date.now()}`;
    const { id: _drop, ...data } = invoice;
    return createWithId<ClientInvoice>('client_invoices', id, data);
  },

  async update(id: string, updates: Partial<ClientInvoice>): Promise<void> {
    const db = getDB();
    await updateDoc(doc(db, 'client_invoices', id), removeUndefined(updates as Record<string, unknown>));
  },

  async delete(id: string): Promise<void> {
    return remove('client_invoices', id);
  },

  subscribe(
    callback: (invoices: ClientInvoice[]) => void,
    onError?: (error: unknown) => void,
  ): Unsubscribe {
    const db = getDB();
    const q = query(collection(db, 'client_invoices'), orderBy('dateCreated', 'desc'));
    return onSnapshot(
      q,
      (snapshot) => {
        callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as ClientInvoice[]);
      },
      (err) => {
        console.warn('[client_invoices] listener:', (err as { code?: string })?.code ?? err);
        onError?.(err);
      },
    );
  },
};

// ============ Manual Sales Overrides (for report backfill when orders are missing) ============
export const manualSalesOverridesService = {
  async get(date: string): Promise<ManualSalesOverride | null> {
    const db = getDB();
    const docRef = doc(db, 'manual_sales_overrides', date);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    const data = docSnap.data();
    return { date: docSnap.id, ...data } as ManualSalesOverride;
  },

  async set(override: { date: string; totalRevenue: number; totalOrders: number; note?: string }): Promise<void> {
    const db = getDB();
    const docRef = doc(db, 'manual_sales_overrides', override.date);
    await setDoc(docRef, {
      totalRevenue: override.totalRevenue,
      totalOrders: override.totalOrders,
      note: override.note ?? null,
      updatedAt: Date.now(),
    }, { merge: true });
  },

  async getRange(dateFrom: string, dateTo: string): Promise<ManualSalesOverride[]> {
    const db = getDB();
    const snapshot = await getDocs(collection(db, 'manual_sales_overrides'));
    return snapshot.docs
      .filter((d) => d.id >= dateFrom && d.id <= dateTo)
      .map((d) => ({ date: d.id, ...d.data() } as ManualSalesOverride));
  },
};



